import $ from '@sparta.global.vendors/jquery';
import spartaStoreUtility from '@sparta.global.utilities/sparta-store-utility/dist/es6.js';
import { svgHelper } from '@sparta.global.helpers/svg-helper/dist/es6.js';
import spartaEventsUtility from '@sparta.global.utilities/sparta-events-utility/dist/es6.js';
import spartaGlobalFunctions from '@sparta.global.utilities/sparta-global-functions-utility/dist/es6.js';
import spartaInputFormattingUtility from '@sparta.global.utilities/sparta-input-formatting-utility/dist/es6.js';
import spartaSingletonUtility from '@sparta.global.utilities/sparta-singleton-utility/dist/es6.js';

/* eslint-disable require-jsdoc */

// Datepicker manager. Use the singleton instance of this class (`datepicker`) to interact
// with the datepicker. Settings for (groups of) datepickers are maintained in an instance object,
// allowing multiple different settings on the same page.
function Datepicker() {
  const FLYOUT_CLASS = 'ui-datepicker';
  this.debug = false; // Change this to true to start debugging.
  this.currentInstance = null; // The current instance in use.
  this.recentKeyEvent = false; // If the last event was a key event.
  this.disabledInputs = []; // List of datepicker inputs that have been disabled.
  this.flyoutIsOpen = false; // True if the popup picker is showing, false if not.
  this.flyoutId = `${FLYOUT_CLASS}-div`; // The ID of the main datepicker flyout.
  this.inlineClass = `${FLYOUT_CLASS}--inline`; // The name of the inline marker class.
  this.appendClass = `${FLYOUT_CLASS}-append`; // The name of the append marker class.
  this.triggerClass = `${FLYOUT_CLASS}-trigger`; // The name of the trigger marker class.
  this.unselectableClass = `${FLYOUT_CLASS}-unselectable`; // The unselectable cell marker class.
  this.todayClass = `${FLYOUT_CLASS}-today`; // Class for today's date.
  this.currentClass = `${FLYOUT_CLASS}-current-day`; // The name of the current day marker class.
  this.currentSelectedClass = `${FLYOUT_CLASS}-selected-day`; // Class for selected day from input.
  this.dayOverClass = `${FLYOUT_CLASS}-days-cell-over`; // The name of the day hover marker class.

  // Global defaults for all the Datepicker instances.
  this.defaults = {
    showAnim: 'show',
    // Name of jQuery animation for flyout
    showOptions: {},
    // Options for enhanced animations
    defaultDate: 'null',
    // Used when field is blank: actual date,
    // +/-number for offset from today, null for today
    appendText: '',
    // Display text following the input box, e.g. showing the format
    hideIfNoPrevNext: true,
    // True to hide next/previous month links
    // if not applicable, false to just disable them
    navigationAsDateFormat: false,
    // True if date formatting applied to prev/today/next links
    gotoCurrent: true,
    // True if today link goes back to current selection instead
    changeMonth: false,
    // True if month can be selected directly, false if only prev/next
    changeYear: false,
    // True if year can be selected directly, false if only prev/next
    showMonthAfterYear: false,
    // True if the year select precedes month, false for month then year
    yearRange: '-10:+10',
    // Range of years to display in drop-down,
    // either relative to current year (-nn:+nn) or absolute (nnnn:nnnn)
    showOtherMonths: false,
    // True to show dates in other months, false to leave blank
    calculateWeek: this.iso8601Week,
    // How to calculate the week of the year,
    // takes a Date and returns the number of the week for it
    shortYearCutoff: '+10',
    // Short year values < this are in the current century,
    // > this are in the previous century,
    // string value starting with '+' for current year + value
    minDate: null,
    // The earliest selectable date, or null for no limit
    maxDate: null,
    // The latest selectable date, or null for no limit
    duration: 'normal',
    // Duration of display/closure
    beforeShowDay: null,
    // Function that takes a date and returns an array with
    // [0] = true if selectable, false if not, [1] = custom CSS class name(s) or '',
    // [2] = cell title (optional), e.g. `datepicker.noWeekends`
    beforeShow: null,
    // Function that takes an input field and
    // returns a set of custom settings for the datepicker
    onSelect: null,
    // Define a callback function when a date is selected
    onChangeMonthYear: null,
    // Define a callback function when the month or year is changed
    onClose: null,
    // Define a callback function when the datepicker is closed
    numberOfMonths: 2,
    // Number of months to show at a time
    showCurrentAtPos: 0,
    // The position in multipe months to show the current month (starting at 0)
    stepMonths: 1,
    // Number of months to step back/forward
    stepBigMonths: 12,
    // Number of months to step back/forward for the big links
    altField: '#actualDate',
    // Selector for an alternate field to store selected dates into
    altFormat: 'mm/dd/yy',
    // The date format to use for the alternate field
    constrainInput: true,
    // The input is constrained by the current date format
    showButtonPanel: false,
    // True to show button panel, false to not show it.
    showWeekend: 1,
    // 1 to keep weekends grayed and 0 to un-gray them
    showHoliday: 1,
    // 1 to keep bank holidays grayed and 0 to un-gray them
    endOfMonthLink: false,
    // End-of-month convenience link
    autoFormatDateInput: false,
    // Auto format date input into mm/dd/yyyy as user types
    addActiveClass: false,
    // Class to add for active selectable date
    legendShow: false // Hide indicator legend on opening DP
  };

  // Available regional settings, indexed by language code.
  this.regional = {
    '': {
      // Default regional settings.
      dateFormat: '',
      // See format options on `parseDate`.
      firstDay: 0,
      // The first day of the week, Sun = 0, Mon = 1...
      isRtl: false // True if right-to-left language, false if left-to-right.
    },
    'es-us': {
      // Spanish language regional settings.
      dateFormat: '',
      firstDay: 0,
      isRtl: false
    }
  };
  $.extend(this.defaults, this.regional['']);
  this.$flyout = $(`
    <div
      id="${this.flyoutId}"
      class="${FLYOUT_CLASS}"
      aria-hidden="true"
    ></div>
  `);
}

/* eslint-disable no-param-reassign */
const FLYOUT_CLASS$1 = 'ui-datepicker';
const WEEK_INDEX_SUNDAY = 0;
const WEEK_INDEX_MONDAY = 1;
const WEEK_INDEX_FRIDAY = 5;
const WEEK_INDEX_SATURDAY = 6;
const INCLUDE_WEEKEND = 0;
const EXCLUDE_WEEKEND = 1;
var helpers = {
  positionFlyout($flyout, $input) {
    const DATEPICKER_LABEL_HEIGHT = 20;
    const flyout = $flyout[0];
    const rect = $input[0].getBoundingClientRect();

    // Shifting flyout -20 pixel relative to its input if viewport is smaller.
    const absLeft = spartaStoreUtility.getData('currentMQ').mediaQueries.smallMid ? 0 // Normal viewport.
    : `-${rect.x - 20}px`; // Smaller viewport.
    flyout.style.left = absLeft;

    // Determining whether to open datepicker above or below input element based on available
    // window space. Setting datepicker to `visibility: hidden` so we can get its height.
    flyout.style.top = '';
    flyout.style.display = 'block';
    flyout.style.visibility = 'hidden';
    if (rect.y + $(flyout).height() > $(window).height()) {
      flyout.style.top = `-${$(flyout).height() - DATEPICKER_LABEL_HEIGHT}px`;
      flyout.classList.add(`${FLYOUT_CLASS$1}--raise-up`);
    } else {
      flyout.style.top = '';
      flyout.classList.remove(`${FLYOUT_CLASS$1}--raise-up`);
    }

    // Reverting datepicker to `display: none` so animation can work.
    flyout.style.display = 'none';
    flyout.style.visibility = 'visible';
  },
  focusTrap($flyout, event) {
    const currEle = event.target;
    const focusableSelect = $flyout.find('select');
    const focusablebutton = $flyout.find('button:not([tabindex=-1])');
    let focusableEle = [];
    if (focusableSelect && focusableSelect.length) {
      focusableEle = [...focusableSelect, ...focusablebutton];
    } else {
      focusableEle = [...focusablebutton];
    }
    const firstFocusableEl = focusableEle[0];
    const lastFocusableEl = focusableEle[focusableEle.length - 1];
    if (event.shiftKey) {
      if (currEle === firstFocusableEl) {
        event.preventDefault();
        lastFocusableEl.focus();
      }
    } else {
      if (currEle === lastFocusableEl) {
        event.preventDefault();
        firstFocusableEl.focus();
      }
    }
  },
  getFlyoutTemplate(tokenClass) {
    const datepicker = core();
    return `
      <div
        class="${tokenClass} ${FLYOUT_CLASS$1} spa-date-input--default-design"
        id="${datepicker.flyoutId}-${tokenClass}"
      ></div>
    `;
  },
  setupMinMaxDates(options) {
    const minDate = this.convertTokenToDate(options.minDate);
    const maxDate = this.convertTokenToDate(options.maxDate);

    // Swap start date and end date if user has provided flipped direction.
    if (minDate.getTime() > maxDate.getTime()) {
      options.minDate = maxDate;
      options.maxDate = minDate;
    } else {
      options.minDate = minDate;
      options.maxDate = maxDate;
    }
  },
  isValidDate(value) {
    return value && typeof value.getTime === 'function' && !Number.isNaN(value.getTime());
  },
  isDateSame(a, b) {
    return this.isValidDate(a) && this.isValidDate(b) && a.getTime() === b.getTime();
  },
  isWeekStartDate(date, options) {
    return date.getDay() === WEEK_INDEX_SUNDAY && options.showWeekend === INCLUDE_WEEKEND || date.getDay() === WEEK_INDEX_MONDAY && options.showWeekend === EXCLUDE_WEEKEND;
  },
  isWeekEndDate(date, options) {
    return date.getDay() === WEEK_INDEX_SATURDAY && options.showWeekend === INCLUDE_WEEKEND || date.getDay() === WEEK_INDEX_FRIDAY && options.showWeekend === EXCLUDE_WEEKEND;
  },
  isWeekSelectable(startDate, options) {
    const endDate = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());
    endDate.setDate(endDate.getDate() + (options.showWeekend === INCLUDE_WEEKEND ? 6 : 4));
    return this.isDateInMinMaxRange(startDate, options) && this.isDateInMinMaxRange(endDate, options);
  },
  isFullYear(year) {
    return String(year).length === 4 && !Number.isNaN(Number(year));
  },
  isBankHoliday(date) {
    const bankHolidays = spartaStoreUtility.getData('bankHolidays') || [];
    for (let i = 0; i < bankHolidays.length; i++) {
      const holiday = new Date(bankHolidays[i]);
      if (date.getTime() === holiday.getTime()) {
        return true;
      }
    }
    return false;
  },
  isDateInMinMaxRange(date, options) {
    return date.getTime() >= options.minDate.getTime() && date.getTime() <= options.maxDate.getTime();
  },
  cloneDate(date) {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate());
  },
  getToday() {
    const date = new Date();
    return new Date(date.getFullYear(), date.getMonth(), date.getDate());
  },
  getMonthIndex(monthName, labels) {
    if (typeof monthName === 'string' && monthName.length > 2) {
      monthName = monthName.toLocaleLowerCase();

      // Try full month name first.
      for (let i = 0; i < labels.monthNames.length; i++) {
        if (labels.monthNames[i].toLocaleLowerCase() === monthName) {
          return i;
        }
      }

      // Try abbreviated month name.
      for (let i = 0; i < labels.monthNamesShort.length; i++) {
        if (labels.monthNamesShort[i].toLocaleLowerCase() === monthName) {
          return i;
        }
      }
    }
    return -1;
  },
  getFirstOfMonth(date) {
    return new Date(date.getFullYear(), date.getMonth(), 1);
  },
  getMMDDYYYString(date) {
    return `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear()}`;
  },
  getConciseWeekString(fromDate, toDate) {
    return `${this.getMMDDYYYString(fromDate)} - ${this.getMMDDYYYString(toDate)}`;
  },
  getVerboseWeekString(fromDate, toDate, labels) {
    const fromText = this.getVerboseDateString(fromDate, labels);
    const toText = this.getVerboseDateString(toDate, labels);
    return `${fromText} ${labels.toForRange} ${toText}`;
  },
  getVerboseDateString(date, labels) {
    return `${labels.monthNames[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
  },
  getDaySpan(from, to) {
    return Math.abs(Math.round((from - to) / (1000 * 60 * 60 * 24))) + 1;
  },
  getWeekEndDate(weekStartDate, options) {
    let weekEndDate;
    if (this.isValidDate(weekStartDate) && this.isWeekStartDate(weekStartDate, options)) {
      weekEndDate = new Date(weekStartDate.getFullYear(), weekStartDate.getMonth(), weekStartDate.getDate());
      weekEndDate.setDate(weekEndDate.getDate() + (options.showWeekend === INCLUDE_WEEKEND ? 6 : 4));
    }
    return weekEndDate;
  },
  convertTokenToDate(dateRangeToken) {
    if (typeof dateRangeToken !== 'string' || !dateRangeToken.length) {
      return null;
    }
    const date = new Date();
    const tokens = dateRangeToken.split(' ');
    let year = date.getFullYear();
    let month = date.getMonth();
    let day = date.getDate();
    for (let i = 0; i < tokens.length; i++) {
      const token = tokens[i];
      const rangeType = token[token.length - 1];
      const count = parseInt(token.substring(0, token.length - 1), 10);
      switch (rangeType) {
        case 'y':
          year += count;
          break;
        case 'm':
          month += count;
          break;
        case 'd':
          day += count;
          break;
      }
    }
    return new Date(year, month, day);
  }
};

/* eslint-disable no-param-reassign */
const DP_CLASS = 'datepicker';
const STATE_CLASS$1 = 'ui-state';
let datepicker$2 = null;
let $focusedEl;
let dp;
let $input;
let $flyout;
let $grid;
let $gridRows;
let $enabledGridCells;
let $selectedGridCell;
function getInput(event) {
  const target = event.target.classList.contains(datepicker$2.triggerClass) ? event.target.parentNode.querySelector(`.${DP_CLASS}`) : event.target;
  return target.tagName.toUpperCase() === 'INPUT' ? target : datepicker$2.lastInput;
}
function getInstanceFromEvent(event) {
  // We have to account for inline datepickers as well as inputs with a flyout.
  const closestDp = $(event.target).closest(`.${DP_CLASS}`).get(0);
  return datepicker$2.getInstance(closestDp || getInput(event));
}

// Sets the focus to a given cell (if specified) or the first focusable cell.
function selectCell($td = null) {
  // Make all of the dates unfocusable.
  const $cellButtons = $grid.find('button');
  $cellButtons.attr('tabindex', -1);

  // Find a cell to select if none was supplied.
  let $viableCell = $td || $grid.find(`.${STATE_CLASS$1}-active`).parent();
  if (!$viableCell.length) {
    $viableCell = $grid.find('[data-selectable]').first();
  }

  // Make only the selected date (or first viable date) focusable.
  $viableCell.find('button').attr('tabindex', 0).focus();
  $grid.attr('aria-activedescendant', $viableCell.attr('id'));
  $selectedGridCell = $viableCell;
}
function updateGridObjects() {
  $grid = $flyout.find('table');
  $gridRows = $grid.find('tbody tr');
  $enabledGridCells = $gridRows.find('[data-selectable]');
  $selectedGridCell = $gridRows.find('[tabindex=0]').parent();
  if (!$selectedGridCell.length) {
    selectCell($enabledGridCells.first());
  }
}
function adjustMonths(offset, value) {
  const {
    drawMonth
  } = dp;
  datepicker$2.adjustDate($input, offset, value);
  updateGridObjects();
  // Focus active date if present or focus date with tabindex 0.
  selectCell($grid.find(`.${STATE_CLASS$1}-active`).parent() || $selectedGridCell);
  return dp.drawMonth !== drawMonth;
}
function moveToNextMonth() {
  return adjustMonths(datepicker$2.get(dp, 'stepMonths'), 'M');
}
function moveToPrevMonth() {
  return adjustMonths(-datepicker$2.get(dp, 'stepMonths'), 'M');
}
function moveToNextYear() {
  return adjustMonths(datepicker$2.get(dp, 'stepMonths'), 'Y');
}
function moveToPrevYear() {
  return adjustMonths(-datepicker$2.get(dp, 'stepMonths'), 'Y');
}
function moveToFirstDayOfMonth() {
  selectCell($enabledGridCells.first());
}
function moveToLastDayOfMonth() {
  selectCell($enabledGridCells.last());
}
function moveToFirstCell() {
  const enabledCellInCurrentRow = $selectedGridCell.parent().find('[data-selectable]');
  selectCell(enabledCellInCurrentRow.first());
}
function moveToLastCell() {
  const enabledCellInCurrentRow = $selectedGridCell.parent().find('[data-selectable]');
  selectCell(enabledCellInCurrentRow.last());
}
function moveLeftOrRight(dir) {
  let cellIndex = $enabledGridCells.index($selectedGridCell);
  cellIndex = dir === 'left' ? cellIndex - 1 : cellIndex + 1;
  if (dir === 'left' && cellIndex < 0) {
    if (moveToPrevMonth()) {
      moveToLastDayOfMonth();
    } else {
      moveToFirstDayOfMonth();
    }
  } else if (dir === 'right' && cellIndex >= $enabledGridCells.length) {
    if (moveToNextMonth()) {
      moveToFirstDayOfMonth();
    } else {
      moveToLastDayOfMonth();
    }
  } else {
    selectCell($($enabledGridCells[cellIndex]));
  }
}
function moveUpOrDown(dir, preservedColIndex = -1) {
  let colIndex;
  let rowIndex;
  if (preservedColIndex < 0) {
    // Determine the column and row from the currently selected cell.
    colIndex = $selectedGridCell.parent().find('td').index($selectedGridCell);
    rowIndex = $gridRows.index($selectedGridCell.parent());
    rowIndex = dir === 'up' ? rowIndex - 1 : rowIndex + 1;
  } else {
    // If the last active cell was from previous month, reset the row counter.
    colIndex = preservedColIndex;
    rowIndex = dir === 'up' ? $gridRows.length - 1 : 0;
  }

  // Step through this month's rows to find a viable cell to select.
  while (dir === 'up' ? rowIndex >= 0 : rowIndex < $gridRows.length) {
    const $cell = $gridRows.eq(rowIndex).find('td').eq(colIndex);
    if ($cell.is('[data-selectable]')) {
      selectCell($cell);
      return;
    }
    rowIndex = dir === 'up' ? rowIndex - 1 : rowIndex + 1;
  }

  // If no viable cell was found, change the month and keep looking.
  let monthChanged = false;
  if (dir === 'up') {
    monthChanged = moveToPrevMonth();
  } else {
    monthChanged = moveToNextMonth();
  }
  if (monthChanged) {
    moveUpOrDown(dir, colIndex);
    return;
  }

  // If we've hit an edge, move no further.
  if (dir === 'up') {
    moveToFirstDayOfMonth();
  } else {
    moveToLastDayOfMonth();
  }
}
function moveToDatepicker() {
  if ($flyout.is(':hidden') && dp.$input.length) {
    // Open the flyout.
    datepicker$2.openDateFlyout(dp.$input[0], true);
  } else {
    // Move focus into the calendar grid.
    selectCell();
  }
}
function handleArrowDown(inline = false) {
  let eventHandled = false;
  if ($focusedEl.hasClass(DP_CLASS) || $focusedEl.hasClass(datepicker$2.triggerClass)) {
    // From the input or the trigger button, move into the calendar.
    moveToDatepicker();
    eventHandled = true;
  } else if ((inline || datepicker$2.flyoutIsOpen) && $focusedEl.hasClass(`${STATE_CLASS$1}-default`)) {
    // From a calendar cell, move down.
    moveUpOrDown('down');
    eventHandled = true;
  } else if ($focusedEl.prop('tagName').toUpperCase() !== 'SELECT') {
    // For other controls besides select boxes, move into the calendar.
    selectCell();
    eventHandled = true;
  }
  return eventHandled;
}
function doKeyDown(event) {
  datepicker$2 = core();
  let eventHandled = false;
  $focusedEl = $(document.activeElement);
  dp = getInstanceFromEvent(event);
  ({
    $flyout,
    $input
  } = dp);
  updateGridObjects();
  dp.recentKeyEvent = true;
  const noAltShiftOrCtrl = !event.altKey && !event.shiftKey && !event.ctrlKey;
  if (event.key === 'ArrowDown') {
    if (noAltShiftOrCtrl) {
      eventHandled = handleArrowDown(dp.inline);
    }
  } else if (dp.inline || datepicker$2.flyoutIsOpen) {
    if (event.key === 'Escape' && !dp.inline) {
      datepicker$2.closeDateFlyout();
      $input.focus();
      eventHandled = true;
    } else if (event.key === 'Tab') {
      helpers.focusTrap($flyout, event);
    } else if (event.key === 'Home') {
      if (noAltShiftOrCtrl) {
        moveToFirstCell();
        eventHandled = true;
      } else if (event.ctrlKey) {
        moveToFirstDayOfMonth();
        eventHandled = true;
      }
    } else if (event.key === 'End') {
      if (noAltShiftOrCtrl) {
        moveToLastCell();
        eventHandled = true;
      } else if (event.ctrlKey) {
        moveToLastDayOfMonth();
        eventHandled = true;
      }
    } else if (noAltShiftOrCtrl && $focusedEl.hasClass(`${STATE_CLASS$1}-default`)) {
      if (event.key === 'ArrowUp') {
        moveUpOrDown('up');
        eventHandled = true;
      } else if (event.key === 'ArrowLeft') {
        moveLeftOrRight('left');
        eventHandled = true;
      } else if (event.key === 'ArrowRight') {
        moveLeftOrRight('right');
        eventHandled = true;
      } else if (event.key === 'PageUp') {
        moveToPrevMonth();
        eventHandled = true;
      } else if (event.key === 'PageDown') {
        moveToNextMonth();
        eventHandled = true;
      }
    } else if (event.shiftKey) {
      if (event.key === 'PageUp') {
        moveToPrevYear();
        eventHandled = true;
      } else if (event.key === 'PageDown') {
        moveToNextYear();
        eventHandled = true;
      }
    }
  }
  if (eventHandled) {
    event.preventDefault();
    event.stopPropagation();
  }
}

/* eslint-disable no-param-reassign */

// jQuery `extend` now ignores nulls!
function extendRemove(target, props) {
  $.extend(target, props);
  Object.keys(props).forEach(name => {
    if (typeof props[name] === 'undefined' || props[name] === null) {
      target[name] = props[name];
    }
  });
  return target;
}

/* global datePickerCardSelected datePickerPage */

// Custom function to allow weekend selections based on the page.
// 0 = Un-gray weekends days.
// 1 = Gray out weekend days.
function checkGrayWeekend() {
  // default to unused value
  let thisShowWeekend = 2;

  // This is custom logic for the `olb-account-transfer` modules. To add logic for another page, add
  // the `datePickerPage` variable to the new page with a unique ID.
  if (typeof datePickerPage !== 'undefined' && datePickerPage === 'olb-account-transfer') {
    const frequencyValue = $('.olb-account-transfer-module .SelectedValue option:selected').val();
    if (frequencyValue !== 'Immediate' && frequencyValue !== 'Once') {
      // If transfer frequency is not "immediate" and not "once", show weekends.
      thisShowWeekend = 0;
    } else if (typeof datePickerCardSelected !== 'undefined' && datePickerCardSelected) {
      // If tranfser account to is credit cards, show weekends.
      thisShowWeekend = 0;
    } else {
      // Default to grayed-out weekends.
      thisShowWeekend = 1;
    }
  } else if (typeof datePickerPage !== 'undefined' && datePickerPage === 'm2m-make-transfer') {
    const frequencyValue = $('.transfer-main-module .m2m-transfer-skin .SelectedValue option:selected').val();
    if (frequencyValue !== 'Immediate' && frequencyValue !== 'Once') {
      // If transfer frequency is not "immediate" and not "once", show weekends.
      thisShowWeekend = 0;
    } else if (typeof datePickerCardSelected !== 'undefined' && datePickerCardSelected) {
      // If tranfser account to is credit cards, show weekends.
      thisShowWeekend = 0;
    } else {
      // Default to grayed-out weekends.
      thisShowWeekend = 1;
    }
  }
  return thisShowWeekend;
}

var uiText = {
  en: {
    calendarLayerSelected: 'Calendar popup selected',
    calendarSummary: 'Calendar popup consisting of a month table of Sunday through Saturday columns, visible on screen.',
    endOfMonth: 'End of month',
    endOfLayer: 'End of popup',
    selectToday: 'Select today,',
    useDateFormat: 'When entering date, please use the format',
    prevMonthView: 'Previous Month View',
    nextMonthView: 'Next Month View',
    prevYearView: 'Previous Year View',
    nextYearView: 'Next Year View',
    unavailable: 'unavailable',
    selectDate: 'Select',
    selectYear: 'Select year',
    selectMonth: 'Select month',
    selected: 'Selected',
    pastDue: 'Past due',
    pastDueDate: 'Past due date',
    downArrowCalendarLayer: 'Use down arrow to expose a calendar popup.',
    doubleTapCalendarLayer: 'Double-tap to expose a calendar popup.',
    closeText: 'Close calendar popup',
    closeToolTipText: 'Close',
    prevMonthText: 'Previous Month',
    nextMonthText: 'Next Month',
    prevYearText: 'Previous Year',
    nextYearText: 'Next Year',
    monthNames: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'],
    monthNamesShort: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
    dayNames: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
    dayNamesShort: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
    dayNamesMin: ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'],
    notAvailDateToolTipText: 'is not available',
    dayAvailInAnotherMonth: 'day is available in another month',
    triggerButtonText: 'Calendar for',
    toForRange: 'to',
    today: 'Today',
    due: 'Due',
    showDateKey: 'Show date key',
    hideDateKey: 'Hide date key'
  },
  es: {
    calendarLayerSelected: 'Ventana emergente para calendario seleccionada',
    calendarSummary: 'Ventana emergente para calendario mensual con tabla y columnas de domingo a sábado.',
    endOfMonth: 'Fin de mes',
    endOfLayer: 'Final de ventana emergente',
    selectToday: 'Seleccionar hoy,',
    useDateFormat: 'Para indicar la fecha utilice el formato',
    prevMonthView: 'Mostrar mes anterior',
    nextMonthView: 'Mostrar mes siguiente',
    prevYearView: 'Mostrar a&ntilde;o anterior',
    nextYearView: 'Mostrar a&ntilde;o siguiente',
    unavailable: 'no disponible',
    selectDate: 'Seleccionar',
    selectYear: 'Seleccionar a&ntilde;o',
    selectMonth: 'Seleccionar mes',
    selected: 'Seleccionada',
    pastDue: 'Pasado debido',
    pastDueDate: 'Pasado debido fecha',
    downArrowCalendarLayer: 'Presione la tecla de flecha hacia abajo para mostrar la ventana emergente de calendario.',
    doubleTapCalendarLayer: 'Presione la pantalla dos veces para mostrar la ventana emergente de calendario.',
    closeText: 'Cerrar Calendario',
    closeToolTipText: 'Cerrar',
    prevMonthText: 'Mes anterior',
    nextMonthText: 'Mes siguiente',
    prevYearText: 'A&ntilde;o anterior',
    nextYearText: 'A&ntilde;o siguiente',
    monthNames: ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'],
    monthNamesShort: ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'],
    dayNames: ['Domingo', 'Lunes', 'Martes', 'Mi&eacute;rcoles', 'Jueves', 'Viernes', 'S&aacute;bado'],
    dayNamesShort: ['Dom', 'Lun', 'Mar', 'Mie', 'Jue', 'Vie', 'Sab'],
    dayNamesMin: ['Do', 'Lu', 'Ma', 'Mi', 'Ju', 'Vi', 'Sa'],
    notAvailDateToolTipText: 'no est&aacute; disponible',
    dayAvailInAnotherMonth: 'd&iacute;a est&aacute; disponible en otro mes',
    triggerButtonText: 'Calendario para',
    toForRange: 'a',
    today: 'Hoy',
    due: 'Debido',
    showDateKey: 'Mostar clave de fecha',
    hideDateKey: 'Ocultar clave de fecha'
  }
};

/* eslint-disable no-nested-ternary */
function generateHtml (dp) {
  const datepicker = core();
  const FLYOUT_CLASS = 'ui-datepicker';
  const FLYOUT_PREV_CLASS = `${FLYOUT_CLASS}-prev`;
  const FLYOUT_NEXT_CLASS = `${FLYOUT_CLASS}-next`;
  const FLYOUT_CLOSE_CLASS = `${FLYOUT_CLASS}-close`;
  const STATE_CLASS = 'ui-state';
  const UI_TEXT = uiText[datepicker.language];
  let today = new Date();
  today = this.daylightSavingAdjust(
  // Clear time.
  new Date(today.getFullYear(), today.getMonth(), today.getDate()));
  const isRtl = this.get(dp, 'isRtl');
  const showButtonPanel = this.get(dp, 'showButtonPanel');
  const hideIfNoPrevNext = this.get(dp, 'hideIfNoPrevNext');
  const navigationAsDateFormat = this.get(dp, 'navigationAsDateFormat');
  const numMonths = this.getNumberOfMonths(dp);
  const showCurrentAtPos = this.get(dp, 'showCurrentAtPos');
  const stepMonths = this.get(dp, 'stepMonths');
  const isMultiMonth = numMonths[0] !== 1 || numMonths[1] !== 1;
  const currentDate = this.daylightSavingAdjust(!dp.currentDay ? new Date(9999, 9, 9) : new Date(dp.currentYear, dp.currentMonth, dp.currentDay));
  const minDate = this.getMinMaxDate(dp, 'min', true);
  const maxDate = this.getMinMaxDate(dp, 'max');
  let drawMonth = dp.drawMonth - showCurrentAtPos;
  let {
    drawYear
  } = dp;
  if (drawMonth < 0) {
    drawMonth += 12;
    drawYear -= 1;
  }
  if (maxDate) {
    let maxDraw = this.daylightSavingAdjust(new Date(maxDate.getFullYear(), maxDate.getMonth() - numMonths[1] + 1, maxDate.getDate()));
    maxDraw = minDate && maxDraw < minDate ? minDate : maxDraw;
    while (this.daylightSavingAdjust(new Date(drawYear, drawMonth, 1)) > maxDraw) {
      drawMonth -= 1;
      if (drawMonth < 0) {
        drawMonth = 11;
        drawYear -= 1;
      }
    }
  }
  dp.drawMonth = drawMonth;
  dp.drawYear = drawYear;
  let prevText = UI_TEXT.prevMonthText;
  prevText = !navigationAsDateFormat ? prevText : this.formatDate(prevText, this.daylightSavingAdjust(new Date(drawYear, drawMonth - stepMonths, 1)), this.getFormatConfig(dp));
  let prev = '';
  if (this.get(dp, 'changeYear') === false || this.get(dp, 'changeMonth') === false) {
    prev = this.canAdjustMonth(dp, -1, drawYear, drawMonth) ? `
        <button
          type="button"
          class="${FLYOUT_PREV_CLASS}"
          title="${prevText}"
        >&nbsp;&nbsp;
          <span class="ada-hidden">${UI_TEXT.prevMonthView}</span>
        </button>
      ` : hideIfNoPrevNext ? '' : `
          <button
            type="button"
            class="${FLYOUT_PREV_CLASS} ui-corner-all ${STATE_CLASS}-disabled"
            tabindex="-1"
            disabled=""
            aria-disabled="true"
            title="${prevText}"
          >&nbsp;&nbsp;
            <span class="ada-hidden">${UI_TEXT.prevMonthView} ${UI_TEXT.unavailable}</span>
          </button>
        `;
  }
  let nextText = UI_TEXT.nextMonthText;
  if (navigationAsDateFormat) {
    nextText = this.formatDate(nextText, this.daylightSavingAdjust(new Date(drawYear, drawMonth + stepMonths, 1)), this.getFormatConfig(dp));
  }
  let next = '';
  if (this.get(dp, 'changeYear') === false || this.get(dp, 'changeMonth') === false) {
    next = this.canAdjustMonth(dp, 1, drawYear, drawMonth) ? `
        <button
          type="button"
          class="${FLYOUT_NEXT_CLASS} ui-corner-all"
          title="${nextText}"
        >&nbsp;&nbsp;
          <span class="ada-hidden">${UI_TEXT.nextMonthView}</span>
        </button>
      ` : hideIfNoPrevNext ? '' : `
          <button
            type="button"
            class="${FLYOUT_NEXT_CLASS} ui-corner-all ${STATE_CLASS}-disabled"
            tabindex="-1"
            disabled=""
            aria-disabled="true"
            title="${nextText}"
          >&nbsp;&nbsp;
            <span class="ada-hidden">${UI_TEXT.nextMonthView} ${UI_TEXT.unavailable}</span>
          </button>
        `;
  }
  const controls = dp.inline ? '' : `
      <button
        type="button"
        title="${UI_TEXT.closeToolTipText}"
        class="${FLYOUT_CLOSE_CLASS}"
      >
        <span class="ada-hidden">${UI_TEXT.closeText}</span>
      </button>
    `;
  const buttonPanel = showButtonPanel // Controls HTML moved below.
  ? `<div class="${FLYOUT_CLASS}-buttonpane ui-widget-content"></div>` : '';
  let firstDay = parseInt(this.get(dp, 'firstDay'), 10);
  firstDay = Number.isNaN(firstDay) ? 0 : firstDay;
  const {
    dayNames,
    dayNamesShort,
    dayNamesMin,
    monthNames,
    monthNamesShort,
    notAvailDateToolTipText
  } = UI_TEXT;
  const beforeShowDay = this.get(dp, 'beforeShowDay');
  const showOtherMonths = this.get(dp, 'showOtherMonths');
  const endDate = dp.endDay ? this.daylightSavingAdjust(new Date(dp.endYear, dp.endMonth, dp.endDay)) : currentDate;
  const defaultDate = this.getDefaultDate(dp);
  const showWeekendIfToday = this.get(dp, 'showWeekendIfToday');

  // Check if weekends are grayed out.
  let thisShowWeekend = checkGrayWeekend();
  if (thisShowWeekend > 1) {
    thisShowWeekend = this.get(dp, 'showWeekend');
  }

  // Calculate warning dates.
  let nextYearWarnings = false;
  const warningDates = dueDate => {
    if (parseInt(dueDate.slice(6, 10)) > drawYear) {
      return '';
    } else if (parseInt(dueDate.slice(6, 10)) < drawYear && parseInt(dueDate.slice(0, 2)) >= drawMonth + 1) {
      nextYearWarnings = true;
      return dueDate;
    } else if (parseInt(dueDate.slice(6, 10)) === drawYear && parseInt(dueDate.slice(0, 2)) <= drawMonth + 1) {
      return dueDate;
    }
  };

  // Get due date of current month.
  const currentDueDate = dp.settings.dueDate && warningDates(dp.settings.dueDate);

  // Display indicator legend below DP for current month if present.
  const indicatorKeys = dp.settings.indicator ? Object.keys(dp.settings.indicator) : '';
  const showHolidayDate = this.get(dp, 'showHoliday');
  const currMonIndicator = indicatorKeys.length && indicatorKeys.filter(val => parseInt(val.slice(0, 2)) === drawMonth + 1 && parseInt(val.slice(6, 10)) === drawYear);
  let showHideLabel = `${UI_TEXT.showDateKey}`;
  const monthLabel = new Date(drawYear, drawMonth).toString().slice(4, 7);
  let legendValLoop = '';
  for (let i = 0; i < currMonIndicator.length; i++) {
    legendValLoop += `<li>
      <span class="${FLYOUT_CLASS}-month">${monthLabel} ${currMonIndicator[i].slice(3, 5)}</span>
      <span class="${FLYOUT_CLASS}-label">${dp.settings.indicator[currMonIndicator[i]]}</span>
    </li>`;
  }
  const legendVal = `
    <div class="${FLYOUT_CLASS}-legendValue">
      <hr>
      ${legendValLoop}
      ${dp.settings.dueDate ? `<div class="${FLYOUT_CLASS}-pastDueIndicator">
          <span>
            <svg width="16" height="16">
              <rect width="16" height="16" rx="2" fill="#F9E7EA"/>
              <path d="M8 16L0 8V16H8Z" fill="#C41230"/>
            </svg>
          </span>
          <span class="${FLYOUT_CLASS}-indicatorKey">${dp.settings.indicatorKey || UI_TEXT.pastDueDate}</span>
        </div>` : ''}
    </div>
  `.trim();
  const legendKey = `<button class="${FLYOUT_CLASS}-legendKey">
    <span class="${FLYOUT_CLASS}-legendShowHide">${showHideLabel}</span>
    <span class="chevron spa-icon icon-chevron-down legend-button-arrow"></span>
  </button>`;
  const legend = currMonIndicator.length ? `<hr class="${FLYOUT_CLASS}-legendHR">${legendKey}${legendVal}` : '';
  let html = buttonPanel;
  for (let row = 0; row < numMonths[0]; row++) {
    let group = '';
    for (let col = 0; col < numMonths[1]; col++) {
      const selectedDate = this.daylightSavingAdjust(new Date(drawYear, drawMonth, dp.selectedDay));
      let calendar = isMultiMonth ? `<div class="${FLYOUT_CLASS}-group">` : '';
      const roleInfo = dp.inline ? '' : 'role="dialog"';
      const monthHeader = this.generateMonthYearHeader(dp, drawMonth, drawYear, minDate, maxDate, selectedDate, row > 0 || col > 0, monthNames, monthNamesShort);
      const daysInMonth = this.getDaysInMonth(drawYear, drawMonth);
      if (drawYear === dp.selectedYear && drawMonth === dp.selectedMonth) {
        dp.selectedDay = Math.min(dp.selectedDay, daysInMonth);
      }
      const leadDays = (this.getFirstDayOfMonth(drawYear, drawMonth) - firstDay + 7) % 7;
      const numRows = isMultiMonth // Calculate the number of rows to generate.
      ? 6 : Math.ceil((leadDays + daysInMonth) / 7);
      let printDate = this.daylightSavingAdjust(new Date(drawYear, drawMonth, 1 - leadDays));
      calendar = `
        ${calendar}
        <section class="content-body" ${roleInfo} aria-modal="true" aria-labelledby="${dp.$flyout[0].id}_label">
          <div class="${FLYOUT_CLASS}-header ui-widget-header ui-helper-clearfix ui-corner-all">
            ${row === 0 ? isRtl ? next : prev : ''}
            ${monthHeader}
            ${row === 0 ? isRtl ? prev : next : ''}
            ${controls}
          </div>
          <table
            class="${FLYOUT_CLASS}-calendar"
            data-month="${drawMonth}"
            data-year="${drawYear}"
            role="grid"
            aria-labelledby="${this.$flyout[0].id}_label"
          >
            <thead>
              <tr>`;
      let headCells = '';
      for (let dow = 0; dow < 7; dow++) {
        // Days of the week.
        const day = (dow + firstDay) % 7;
        const headerCssClasses = [`${FLYOUT_CLASS}-row-break`];
        if ((dow + firstDay + 6) % 7 >= 5) {
          headerCssClasses.push(`${FLYOUT_CLASS}-weekend`);
          !thisShowWeekend && headerCssClasses.push(`${FLYOUT_CLASS}-weekend--selectable`);
        }
        const dayNameStyle = !spartaStoreUtility.getData('currentMQ').mediaQueries.smallPlus ? dayNamesMin[day] : dayNamesShort[day];
        headCells = `
          ${headCells}
          <th scope="col" class="${headerCssClasses.join(' ')}">
            <abbr title="${dayNames[day]}">
              <span aria-hidden="true">${dayNameStyle}</span>
              <span class="ada-hidden">${dayNames[day]}</span>
            </abbr>
          </th>
        `;
      }
      calendar = `
        ${calendar}
                ${headCells}
              </tr>
            </thead>
            <tbody>
      `;
      let addActiveClass = this.get(dp, 'addActiveClass');
      for (let dRow = 0; dRow < numRows; dRow++) {
        // Create datepicker rows.
        calendar = `
          ${calendar}
                <tr>
        `;
        let bodyCells = '';
        for (let dow = 0; dow < 7; dow++) {
          // Create datepicker days.
          const daySettings = beforeShowDay ? beforeShowDay.call(dp.$input.length ? dp.$input[0] : null, printDate, currentDueDate, indicatorKeys, showHolidayDate) : [true, ''];
          const isWeekend = (dow + firstDay + 6) % 7 >= 5;
          const isWeekendSelectable = isWeekend && (!thisShowWeekend || showWeekendIfToday && defaultDate.getTime() === printDate.getTime());
          const otherMonth = printDate.getMonth() !== drawMonth;
          const otherMonthSelectable = printDate >= minDate && printDate <= maxDate;
          let bankHolidayPrintDate = '';
          if (daySettings[1].toString().includes(`${FLYOUT_CLASS}-unselectable`)) {
            bankHolidayPrintDate = printDate;
          }
          const unselectable = isWeekend && !isWeekendSelectable || bankHolidayPrintDate || otherMonth || !daySettings[0] || minDate && printDate < minDate || maxDate && printDate > maxDate;
          const isSelected = printDate.getTime() === currentDate.getTime() && printDate.getTime() <= endDate.getTime() && dp.isDateSelected;

          // Get dateText for printDate.
          const printDateText = this.formatDate(this.get(dp, 'altFormat'), printDate);

          // Add active class to next available day if today is unavailable and input has no selected date.
          let activeClass = '';
          if (currentDate.getTime() === today.getTime() && beforeShowDay(today, null, null, showHolidayDate).toString().includes(`${FLYOUT_CLASS}-unselectable`) && !addActiveClass && today.getTime() < printDate.getTime() && !unselectable) {
            activeClass = ` ${STATE_CLASS}-active`;
            addActiveClass = true;
          }

          // Set label.
          let label = '';
          if (daySettings[1].toString().includes(`${FLYOUT_CLASS}-today`)) {
            label = `${UI_TEXT.today}`;
          } else if (daySettings[1].toString().includes(`${FLYOUT_CLASS}-due`)) {
            label = `${UI_TEXT.due}`;
          }

          // Set label class if available.
          let labelSpan = '';
          if (label) {
            labelSpan = `${FLYOUT_CLASS}-labelSpan`;
          }

          // Set indicator if present.
          let indicator = false;
          let indicatorSpan = '';
          let indicatorLabel = '';
          if (daySettings[1].toString().includes(`${FLYOUT_CLASS}-indicate`)) {
            indicator = true;
            indicatorSpan = `${FLYOUT_CLASS}-indicatorSpan`;
            indicatorLabel = dp.settings.indicator[printDateText];
          }

          // Determine which classes and attributes to include on the table cell.
          const weekendClass = isWeekend && !isWeekendSelectable ? `${FLYOUT_CLASS}-weekend` : '';
          const pastWeekendClass = isWeekend && printDate.getTime() <= defaultDate.getTime() ? `${FLYOUT_CLASS}-past-weekend` : '';
          const otherMonthClass = otherMonth && otherMonthSelectable ? `${FLYOUT_CLASS}-other-month` : '';
          const warningClass = currentDueDate && (currentDueDate < printDateText || nextYearWarnings) ? `${FLYOUT_CLASS}-warning` : '';
          const highlightSelectedClass = printDate.getTime() === selectedDate.getTime() && drawMonth === dp.selectedMonth && dp.recentKeyEvent || defaultDate.getTime() === printDate.getTime() && defaultDate.getTime() === selectedDate.getTime() ? this.dayOverClass : '';
          const highlightUnselectableClass = unselectable ? ` ${this.unselectableClass} ` : '';
          const highlightCustomClass = daySettings[0] ? daySettings.length > 1 ? daySettings[1] : '' : daySettings[1];
          const highlightCurrentDayClass = printDate.getTime() >= currentDate.getTime() && printDate.getTime() <= endDate.getTime() ? this.currentClass : '';
          const highlightCurrentSelectedClass = isSelected ? this.currentSelectedClass : '';
          const highlightTodayClass = printDate.getTime() === today.getTime() ? this.todayClass : '';
          const titleAttr = (!otherMonth || showOtherMonths) && daySettings[2] ? `title="${daySettings[2].getDate()}` : '';
          const selectableAttr = unselectable ? '' : 'data-selectable="true"';
          let monthContent = '&nbsp;';
          if (showOtherMonths) {
            const adaHiddenText = isWeekendSelectable || !isWeekend && otherMonthSelectable ? ` ${UI_TEXT.dayAvailInAnotherMonth}` // TODO: Verify Spanish.
            : notAvailDateToolTipText;
            monthContent = `
              <span class="ada-hidden">${monthNames[printDate.getMonth()]} </span>
              <span class="${STATE_CLASS}-default">${printDate.getDate()}</span>
              <span class="ada-hidden"> ${adaHiddenText}</span>
            `;
          }
          const dateContent = `
            <span class="${STATE_CLASS}-printDate" aria-hidden="true">${printDate.getDate()}</span>
            ${label ? `<span class="${labelSpan}" aria-hidden="true">${label}</span>` : ''}
            ${indicator ? `<span class="${indicatorSpan}" aria-hidden="true"></span>` : ''}
          `;
          let cellContent = '';
          if (unselectable) {
            // For a static date cell.
            cellContent = `
              <span class="${STATE_CLASS}-default">
                ${dateContent}
              </span>
            `;
          } else {
            // For a selectable (button link) date cell.
            const cellContentClasses = printDate.getTime() >= currentDate.getTime() && printDate.getTime() <= endDate.getTime() || printDate.getTime() === today.getTime() && printDate.getTime() >= currentDate.getTime() && printDate.getTime() <= endDate.getTime() ? `${STATE_CLASS}-default ${STATE_CLASS}-active` : printDate.getTime() === today.getTime() ? `${STATE_CLASS}-default ${STATE_CLASS}-highlight` : `${STATE_CLASS}-default`;
            printDate.getTime() === today.getTime() ? ` ${UI_TEXT.selectToday} ${dayNames[printDate.getDay()]},` // For today.
            : printDate.getTime() >= currentDate.getTime() && printDate.getTime() <= endDate.getTime() || printDate.getTime() === today.getTime() && printDate.getTime() >= currentDate.getTime() && printDate.getTime() <= endDate.getTime() ? `${UI_TEXT.selected} ${dayNames[printDate.getDay()]}` // For selected day.
            : `${UI_TEXT.selectDate} ${dayNames[printDate.getDay()]},`; // For any day.

            const day = dayNames[printDate.getDay()];
            const month = monthNames[printDate.getMonth()];
            const ariaLabel = `${UI_TEXT.selectDate} ${day} ${month}` + ` ${printDate.getDate()}, ${printDate.getFullYear()} ${label}` + ` ${warningClass ? UI_TEXT.pastDue : ''} ${indicatorLabel}`.trim();
            cellContent = `
              <button
                type="button"
                class="${cellContentClasses}${activeClass}"
                tabindex="${isSelected ? 0 : -1}"
                name="date_selected"
                aria-label="${ariaLabel}"
              >
                ${dateContent}
                ${isSelected && warningClass ? `<span class="ui-datepicker-warning-white-line ${dayNames[printDate.getDay()]}">${svgHelper({
              type: 'static',
              width: '100%',
              height: '100%',
              viewbox: '0 0 10 16',
              fill: 'color-brand-tertiary',
              path: 'M9.27355 11.293L-1 1.01942L-0.27355 0.292969L10 10.5665L9.27355 11.293Z',
              ariaHidden: true
            })}</span>` : ''}
                ${warningClass ? `<span class="ui-datepicker-warning-flag ${dayNames[printDate.getDay()]}">${svgHelper({
              type: 'static',
              width: '100%',
              height: '100%',
              viewbox: '0 0 8.22 8.22',
              fill: 'color-regal-one',
              path: 'M8.24618 10.2657L0.0273438 2.04688V10.2657H8.24618Z',
              ariaHidden: true
            })}</span>` : ''}
              </button>
            `;
          }
          bodyCells = `
            ${bodyCells}
            <td
              id="ts_${printDate.getTime()}"
              class="
                ${weekendClass}
                ${pastWeekendClass}
                ${otherMonthClass}
                ${highlightSelectedClass}
                ${highlightUnselectableClass}
                ${highlightCustomClass}
                ${highlightCurrentDayClass}
                ${highlightCurrentSelectedClass}
                ${highlightTodayClass}
                ${warningClass}
              "
              ${titleAttr}
              ${selectableAttr}
              ${isSelected ? 'aria-selected="true"' : ''}
            >
              ${otherMonth ? monthContent : cellContent}
            </td>
          `;

          // Display for this month.
          printDate.setDate(printDate.getDate() + 1);
          printDate = this.daylightSavingAdjust(printDate);
        }
        calendar = `
          ${calendar}
                  ${bodyCells}
                </tr>`;
      }
      drawMonth += 1;
      if (drawMonth > 11) {
        drawMonth = 0;
        drawYear += 1;
      }

      // Close remaining open block elements.
      calendar = `
        ${calendar}
            </tbody>
          </table>
        </section>
      `;
      if (isMultiMonth) {
        calendar = `${calendar}</div>`;
      }
      group = `
        ${group}
        ${calendar}
        ${legend}
      `;
    }
    html = `
      ${html}
      ${group}
    `;
  }
  if (this.get(dp, 'endOfMonthLink')) {
    html = `
      ${html}
      <div class="${FLYOUT_CLASS}-bottom-link">
        <button type="button" id="${FLYOUT_CLASS}-eom-link">${UI_TEXT.endOfMonth}</button>
      </div>
    `;
  }
  html = `
    ${html}
    <div class="boa-ada-end ada-hidden">${UI_TEXT.endOfLayer}.</div>
  `;
  dp.recentKeyEvent = false;
  return html;
}

/* eslint-disable require-jsdoc */
function updateDatepicker (dp) {
  const FLYOUT_CLASS = 'ui-datepicker';
  const FLYOUT_PREV_CLASS = `${FLYOUT_CLASS}-prev`;
  const FLYOUT_NEXT_CLASS = `${FLYOUT_CLASS}-next`;
  const FLYOUT_CLOSE_CLASS = `${FLYOUT_CLASS}-close`;
  const STATE_CLASS = 'ui-state';
  const datepicker = core();
  const dims = {
    width: dp.$flyout.width() + 4,
    height: dp.$flyout.height() + 4
  };
  const stepMonths = this.get(dp, 'stepMonths');
  const UI_TEXT = uiText[datepicker.language];
  dp.$flyout.removeClass(function removeFlyoutClass() {
    const oldClass = $(this).attr('class').match(/(^|\s)spa-date-input--[^\s$]*/);
    return oldClass ? oldClass[0] : '';
  }).addClass(dp.settings.skin).empty().append(this.generateHtml(dp));
  dp.$flyout.find(`iframe.${FLYOUT_CLASS}-cover`).css({
    width: dims.width,
    height: dims.height
  });
  dp.$flyout.find(`.${FLYOUT_CLOSE_CLASS}`).off('click').on('click', () => {
    datepicker.closeDateFlyout();
  });
  dp.$flyout.find(`.${FLYOUT_PREV_CLASS}, .${FLYOUT_NEXT_CLASS}`).off('click').on('click', e => {
    let direction = '-';
    let identifier = FLYOUT_PREV_CLASS;
    if ($(e.target).hasClass(FLYOUT_NEXT_CLASS)) {
      direction = '+';
      identifier = FLYOUT_NEXT_CLASS;
    }
    dp.$input.first().data('eventTargetElement', identifier);
    datepicker.adjustDate(`#${dp.id}`, Number(`${direction}${stepMonths}`), 'M');
  });
  dp.$flyout.find(`.${FLYOUT_PREV_CLASS}.${STATE_CLASS}-disabled`).addClass(`${FLYOUT_PREV_CLASS}--disabled`);
  dp.$flyout.find(`.${FLYOUT_NEXT_CLASS}.${STATE_CLASS}-disabled`).addClass(`${FLYOUT_NEXT_CLASS}--disabled`);
  dp.$flyout.find(`[data-selectable="true"] > button.${STATE_CLASS}-default`).off('click').on('click', e => {
    const $dateButton = e.target.tagName === 'SPAN' ? $(e.target.parentNode) : $(e.target);
    const drawMonth = $dateButton.closest('[data-month]').attr('data-month');
    const drawYear = $dateButton.closest('[data-year]').attr('data-year');
    const isWarningDate = $dateButton.parent().hasClass('ui-datepicker-warning');
    datepicker.selectDay(`#${dp.id}`, drawMonth, drawYear, $dateButton[0].parentNode, isWarningDate);
  });
  let legendShow = this.get(dp, 'legendShow');

  // To show indicator legend value.
  function onLegendShow() {
    dp.$flyout.find(`.${FLYOUT_CLASS}-legendValue`).show();
    dp.$flyout.find('.legend-button-arrow').addClass('active');
    dp.$flyout.find(`.${FLYOUT_CLASS}-legendShowHide`).html(`${UI_TEXT.hideDateKey}`);
    legendShow = true;
  }

  // To hide indicator legend value.
  function onLegendHide() {
    dp.$flyout.find(`.${FLYOUT_CLASS}-legendValue`).hide();
    dp.$flyout.find('.legend-button-arrow').removeClass('active');
    dp.$flyout.find(`.${FLYOUT_CLASS}-legendShowHide`).html(`${UI_TEXT.showDateKey}`);
    legendShow = false;
  }

  // Maintain legend toggle state.
  const legendObj = dp.legend || {};
  const legendCurrMon = Object.keys(legendObj).find(ele => ele === dp.drawMonth.toString());
  if (legendObj[legendCurrMon]) {
    onLegendShow();
  } else {
    onLegendHide();
  }

  // Indicator Legend toggle.
  dp.$flyout.find(`.${FLYOUT_CLASS}-legendKey`).off('click').on('click', () => {
    if (!legendShow) {
      onLegendShow();
    } else {
      onLegendHide();
    }
    dp.legend[dp.drawMonth] = legendShow;
  });
  dp.$flyout.find(`select.${FLYOUT_CLASS}-month, select.${FLYOUT_CLASS}-year`).off('change').on('change', e => {
    const isMonth = $(e.target).hasClass(`${FLYOUT_CLASS}-month`);
    datepicker.selectMonthYear(`#${dp.id}`, e.target, isMonth ? 'M' : 'Y');

    // Restore focus after updating the flyout.
    dp.$flyout.find(`select.${FLYOUT_CLASS}-${isMonth ? 'month' : 'year'}`).focus();
  });
  dp.$flyout.find(`#${FLYOUT_CLASS}-eom-link`).off('click').on('click', e => {
    $(e.target).parent().siblings(`.${FLYOUT_CLASS}-calendar`).find('button').last().trigger('click');
  });
  dp.$flyout.find('button').on('blur', datepicker.doBlur);
  const numMonths = this.getNumberOfMonths(dp);
  const cols = numMonths[1];
  if (cols <= 0) {
    dp.$flyout.removeClass(`${FLYOUT_CLASS}-multi-2 ${FLYOUT_CLASS}-multi-3 ${FLYOUT_CLASS}-multi-4`).width('');
  }
  dp.$flyout.addClass(`${FLYOUT_CLASS}-multi-${cols}`).css('height', 'auto').toggleClass(`${FLYOUT_CLASS}-multi`, numMonths[0] !== 1 || numMonths[1] !== 1).toggleClass(`${FLYOUT_CLASS}-rtl`, !!this.get(dp, 'isRtl'));

  // Retaining focus when month is adjusted through keyboard.
  const dpNotHidden = dp.$input.length && dp.$input[0].type !== 'hidden';
  if (dpNotHidden && (dp.inline || dp === datepicker.currentInstance)) {
    const $target = dp.$input.first();
    const eventTargetElement = $target.data('eventTargetElement');

    // Handle focus for certain links.
    if (eventTargetElement) {
      const $mainDiv = dp.inline ? $target : $(`#${datepicker.flyoutId}`);
      const $nextMonth = $mainDiv.find(`.${FLYOUT_NEXT_CLASS}`);
      const $prevMonth = $mainDiv.find(`.${FLYOUT_PREV_CLASS}`);
      if (eventTargetElement === FLYOUT_NEXT_CLASS) {
        if (!$nextMonth.hasClass(`${STATE_CLASS}-disabled`)) {
          $nextMonth.focus();
        } else if (!$prevMonth.hasClass(`${STATE_CLASS}-disabled`)) {
          $prevMonth.focus();
        }
      } else if (eventTargetElement === FLYOUT_PREV_CLASS) {
        if (!$prevMonth.hasClass(`${STATE_CLASS}-disabled`)) {
          $prevMonth.focus();
        } else if (!$nextMonth.hasClass(`${STATE_CLASS}-disabled`)) {
          $nextMonth.focus();
        }
      }
    }
  }
}

/* eslint-disable no-nested-ternary */

/*
 * Based on jQuery UI Datepicker 1.7.2
 *
 * Copyright (c) 2009 AUTHORS.txt (http://jqueryui.com/about)
 * Dual licensed under the MIT (MIT-LICENSE.txt)
 * and GPL (GPL-LICENSE.txt) licenses.
 *
 * http://docs.jquery.com/UI/Datepicker
 *
 * Large rewrite 20140707 - Jason Powell. 100% overhaul of accessibility features, keyboard
 * functionality & ADA screenreader content. Update per Ux 3.6 style guide and added calendar icon
 * to input field.
 */

// Keep the same reference to this object no matter how many times the exported function is called.
let datepicker$1 = null;
function core () {
  // No need to redefine this Datepicker object if it's already been set up.
  if (datepicker$1) {
    return datepicker$1;
  }
  $.extend($.ui, {
    datepicker: {
      version: '1.7.2'
    }
  });
  const PROP_NAME = 'datepicker';
  const INPUT_CLASS = 'datepicker';
  const FLYOUT_CLASS = 'ui-datepicker';
  const STATE_CLASS = 'ui-state';
  const HELP_TRIGGER_CLASS = 'toggle-trigger';

  $.extend(Datepicker.prototype, {
    // Class name added to elements to indicate already configured with a datepicker.
    markerClassName: 'hasDatepicker',
    // Debug logging (if enabled).
    log(...args) {
      if (this.debug) {
        // eslint-disable-next-line no-console
        console.log.call(this, ...args);
      }
    },
    /* Override the default settings for all instances of the datepicker.
      @param  settings  object - the new settings to use as defaults (anonymous object)
      @return the manager object */
    setDefaults(settings) {
      extendRemove(this.defaults, settings || {});
      return this;
    },
    /* Attach the datepicker to a jQuery selection.
      @param  target    element - the target input field or division or span
      @param  settings  object - the new settings to use for this datepicker instance (anonymous) */
    attachDatepicker(target, settings) {
      // check for settings on the control itself - in namespace 'date:'
      let inlineSettings = null;
      Object.keys(this.defaults).forEach(attrName => {
        const attrValue = target.getAttribute(`date:${attrName}`);
        if (attrValue) {
          inlineSettings = inlineSettings || {};
          try {
            // eslint-disable-next-line no-eval
            inlineSettings[attrName] = eval(attrValue);
          } catch (err) {
            inlineSettings[attrName] = attrValue;
          }
        }
      });
      const tagName = target.tagName.toUpperCase();
      const inline = tagName === 'DIV' || tagName === 'SPAN';
      if (!target.id) {
        this.uuid += 1;
        target.id = `dp${this.uuid}`;
      }
      const dp = this.newInstance($(target), inline);
      dp.settings = $.extend({}, settings || {}, inlineSettings || {});
      dp.legend = {};
      if (tagName === 'INPUT') {
        this.connectDatepicker(target, dp);
      } else if (inline) {
        this.inlineDatepicker(target, dp);
      }
    },
    // Create a new instance object.
    newInstance($target, inline) {
      const id = $target[0].id.replace(/([:[\].])/g, '\\\\$1'); // Escape jQuery meta characters.
      return {
        id,
        $input: $target,
        selectedDay: 0,
        selectedMonth: 0,
        selectedYear: 0,
        isDateSelected: false,
        drawMonth: 0,
        drawYear: 0,
        inline,
        $flyout: inline ? $(`
              <div
                class="
                  ${FLYOUT_CLASS}
                  ${this.inlineClass}
                  ui-widget
                  ui-widget-content
                  ui-helper-clearfix
                  ui-corner-all
                "
              ></div>
            `) : this.$flyout
      };
    },
    // Attach the datepicker to an input field.
    connectDatepicker(target, dp) {
      const $input = $(target);
      const $triggerButton = $input.siblings(`.${this.triggerClass}`);
      dp.append = $([]);
      dp.trigger = $([]);
      if ($input.hasClass(this.markerClassName)) {
        // Skip adding the datepicker if it's already present.
        return;
      }
      const appendText = this.get(dp, 'appendText');
      const isRtl = this.get(dp, 'isRtl');
      if (appendText) {
        dp.append = $(`<span class="${this.appendClass}">${appendText}</span>`);
        $input[isRtl ? 'before' : 'after'](dp.append);
      }
      $input.addClass(this.markerClassName).off('keydown').on('keydown', this.doKeyDown).off(`setData.${PROP_NAME}`).on(`setData.${PROP_NAME}`, (event, key, value) => {
        dp.settings[key] = value;
      }).off(`getData.${PROP_NAME}`).on(`getData.${PROP_NAME}`, function inputGetData(event, key) {
        return this.get(dp, key);
      }).data(PROP_NAME, dp);

      // Toggle the datepicker when the calendar icon is clicked.
      $triggerButton.off('click').on('click', e => {
        if (this.flyoutIsOpen && this.lastInput === target) {
          this.closeDateFlyout();
        } else {
          this.openDateFlyout(e.target, true);
        }
      }).off('keydown').on('keydown', this.doKeyDown);
      dp.$flyout.off('keydown').on('keydown', this.doKeyDown);
    },
    // Attach an inline datepicker to a `div` or `span`.
    inlineDatepicker(target, dp) {
      const $target = $(target);
      if ($target.hasClass(this.markerClassName)) {
        // Skip adding the datepicker if it's already present.
        return;
      }
      $target.addClass(this.markerClassName).append(dp.$flyout).off(`setData.${PROP_NAME}`).on(`setData.${PROP_NAME}`, (event, key, value) => {
        dp.settings[key] = value;
      }).off(`getData.${PROP_NAME}`).on(`getData.${PROP_NAME}`, function targetGetData(event, key) {
        return this.get(dp, key);
      }).data(PROP_NAME, dp);
      this.setDate(dp, this.getDefaultDate(dp));
      this.updateDatepicker(dp);
      this.updateAlternate(dp);

      // We don't have any input field or trigger button, so just attach this one listener.
      dp.$flyout.off('keydown').on('keydown', this.doKeyDown);
    },
    /* Detach a datepicker from its control.
      @param  target    element - the target input field or division or span */
    destroyDatepicker(target) {
      const $target = $(target);
      const dp = $target.data(PROP_NAME);
      if (!$target.hasClass(this.markerClassName)) {
        return;
      }
      const tagName = target.tagName.toUpperCase();
      $target.removeData(PROP_NAME);
      if (tagName === 'INPUT') {
        dp.append.remove();
        dp.trigger.remove();
        $target.removeClass(this.markerClassName).off('keydown');
        $target.siblings(`.${this.triggerClass}`).removeClass(this.markerClassName).off('click').off('keydown');
      } else if (tagName === 'DIV' || tagName === 'SPAN') {
        $target.removeClass(this.markerClassName).empty();
      }
    },
    /* Enable the datepicker to a jQuery selection.
      @param  target    element - the target input field or division or span */
    enableDatepicker(target) {
      const $target = $(target);
      const dp = $target.data(PROP_NAME);
      if (!$target.hasClass(this.markerClassName)) {
        return;
      }
      const tagName = target.tagName.toUpperCase();
      if (tagName === 'INPUT') {
        target.disabled = false;
        dp.trigger.filter('button').each(function eachButton() {
          this.disabled = false;
        });
        dp.trigger.filter('img').css({
          opacity: '1',
          cursor: ''
        });
      } else if (tagName === 'DIV' || tagName === 'SPAN') {
        const inline = $target.children(`.${this.inlineClass}`);
        inline.children().removeClass(`${STATE_CLASS}-disabled`);
      }
      this.disabledInputs = $.map(this.disabledInputs, value => value === target ? null : value); // delete entry
    },
    /* Disable the datepicker to a jQuery selection.
      @param  target    element - the target input field or division or span */
    disableDatepicker(target) {
      const $target = $(target);
      const dp = $target.data(PROP_NAME);
      if (!$target.hasClass(this.markerClassName)) {
        return;
      }
      const tagName = target.tagName.toUpperCase();
      if (tagName === 'INPUT') {
        target.disabled = true;
        dp.trigger.filter('button').each(function eachButton() {
          this.disabled = true;
        });
        dp.trigger.filter('img').css({
          opacity: '0.5',
          cursor: 'default'
        });
      } else if (tagName === 'DIV' || tagName === 'SPAN') {
        const inline = $target.children(`.${this.inlineClass}`);
        inline.children().addClass(`${STATE_CLASS}-disabled`);
      }
      this.disabledInputs = $.map(this.disabledInputs, value => value === target ? null : value // delete entry
      );
      this.disabledInputs[this.disabledInputs.length] = target;
    },
    /* Is the first field in a jQuery collection disabled as a datepicker?
      @param  target    element - the target input field or division or span
      @return boolean - true if disabled, false if enabled */
    isDisabledDatepicker(target) {
      if (!target) {
        return false;
      }
      for (let i = 0; i < this.disabledInputs.length; i++) {
        if (this.disabledInputs[i] === target) {
          return true;
        }
      }
      return false;
    },
    /* Retrieve the instance data for the target control.
      @param  target  element - the target input field or division or span
      @return  object - the associated instance data
      @throws  error if a jQuery problem getting data */
    getInstance(target) {
      try {
        return $(target).data(PROP_NAME);
      } catch (err) {
        throw new Error('Missing instance data for this datepicker');
      }
    },
    /* Update or retrieve the settings for a date picker attached to an input field or division.
       @param  target  element - the target input field or division or span
       @param  name    object - the new settings to update or
               string - the name of the setting to change or retrieve,
               when retrieving also 'all' for all instance settings or
               'defaults' for all global defaults
       @param  value   any - the new value for the setting
               (omit if above is an object or to retrieve a value) */
    optionDatepicker: function (target, name, value) {
      var inst = this.getInstance(target);
      if (arguments.length == 2 && typeof name == 'string') {
        return name == 'defaults' ? $.extend({}, datepicker$1.defaults) : inst ? name == 'all' ? $.extend({}, inst.settings) : this.get(inst, name) : null;
      }
      var settings = name || {};
      if (typeof name == 'string') {
        settings = {};
        settings[name] = value;
      }
      if (inst) {
        // if (this._curInst == inst) {
        // 	this.hideDatepicker(null);
        // }
        var date = this.getDateDatepicker(target);
        extendRemove(inst.settings, settings);
        this.setDateDatepicker(target, date);
        this.updateDatepicker(inst);
      }
    },
    /* Redraw the datepicker attached to an input field or division.
      @param  target  element - the target input field or division or span */
    refreshDatepicker(target) {
      const dp = this.getInstance(target);
      if (dp) {
        this.updateDatepicker(dp);
      }
    },
    /* Set the dates for a jQuery selection.
      @param  target   element - the target input field or division or span
      @param  date     Date - the new date
      @param  endDate  Date - the new end date for a range (optional) */
    setDateDatepicker(target, date, endDate) {
      const dp = this.getInstance(target);
      if (dp) {
        this.setDate(dp, date, endDate);
        this.updateDatepicker(dp);
        this.updateAlternate(dp);
      }
    },
    /* Get the date(s) for the first entry in a jQuery selection.
      @param  target  element - the target input field or division or span
      @return Date - the current date or
              Date[2] - the current dates for a range */
    getDateDatepicker(target) {
      const dp = this.getInstance(target);
      if (dp && !dp.inline) {
        this.setDateFromField(dp);
      }
      return dp ? this.getDate(dp) : null;
    },
    // Handles keystrokes.
    doKeyDown,
    // Old function, no longer needed as we now have a focus trap
    doBlur() {},
    /* Pop-up the datepicker for a given input field.
      @param  input  element - the input field attached to the datepicker or
                      event - if triggered by focus */
    openDateFlyout(input, setFocus = false) {
      // Find the input field from an icon or button click.
      if (input.classList.contains('datepicker-icon--calendar')) {
        input = input.parentNode;
      }
      if (!input.classList.contains(INPUT_CLASS)) {
        input = input.parentNode.querySelector(`.${INPUT_CLASS}`);
      }

      // Mark the trigger button as "expanded" since it's a toggle.
      const triggerButton = input.parentNode.querySelector(`.${this.triggerClass}`);
      if (triggerButton) {
        triggerButton.setAttribute('aria-expanded', 'true');
      }

      // If there's nothing to do, exit early.
      if (datepicker$1.isDisabledDatepicker(input) || datepicker$1.lastInput === input) {
        return;
      }

      // Retrieve a reference to the current active datepicker.
      const dp = datepicker$1.getInstance(input);
      if (!dp) {
        return;
      }
      const beforeShow = datepicker$1.get(dp, 'beforeShow');
      extendRemove(dp.settings, beforeShow ? beforeShow.call(input, input, dp) : {});
      dp.prevVal = dp.$input.val();
      datepicker$1.closeDateFlyout();
      datepicker$1.lastInput = input;
      datepicker$1.setDateFromField(dp);
      dp.rangeStart = null;
      datepicker$1.updateDatepicker(dp);

      // Flush the datepicker flyout to the left if viewport width is smaller than 375px.
      const flyout = dp.$flyout[0];
      flyout.style.top = '';
      const rect = input.getBoundingClientRect();
      const absLeft = !spartaStoreUtility.getData('currentMQ').mediaQueries.smallMid ? `-${rect.x - 20}px` // Smaller viewport.
      : 0; // Larger viewport.
      flyout.style.left = absLeft;

      // Set datepicker to `visibility: hidden` so we can get its height.
      flyout.style.display = 'block';
      flyout.style.visibility = 'hidden';

      // Determine whether to open datepicker above or below input based on available window space.
      const DATEPICKER_LABEL_HEIGHT = 20;
      if (rect.y + $(flyout).height() > $(window).height()) {
        flyout.style.top = `-${$(flyout).height() - DATEPICKER_LABEL_HEIGHT}px`;
        flyout.classList.add(`${FLYOUT_CLASS}--raise-up`);
      } else {
        flyout.style.top = '';
        flyout.classList.remove(`${FLYOUT_CLASS}--raise-up`);
      }

      // Revert datepicker to `display: none` so animation can work.
      flyout.style.display = 'none';
      flyout.style.visibility = 'visible';

      // If the input has a `data-position` attribute add a class to the flyout so we can target it.
      const customPosition = input.getAttribute('position');
      if (customPosition) {
        flyout.classList.add(`${FLYOUT_CLASS}-position--${customPosition}`);
      }
      if (!dp.inline) {
        const showAnim = datepicker$1.get(dp, 'showAnim') || 'show';
        const duration = datepicker$1.get(dp, 'duration');
        const $thisInput = dp.$input.first();
        const postProcess = () => {
          datepicker$1.flyoutIsOpen = true;
          flyout.setAttribute('aria-hidden', 'false');
          if (setFocus) {
            // Move focus to single focusable date.
            const activeDate = dp.$flyout.find(`.${STATE_CLASS}-active`);
            const selectableDate = dp.$flyout.find('[data-selectable]').first().find('button');
            const focusDate = activeDate.length ? activeDate : selectableDate;
            focusDate.attr('tabindex', 0).focus();
          }
        };

        // If there is no custom position specified, remove this `relative` marker.
        if (!customPosition) {
          flyout.classList.remove(`${FLYOUT_CLASS}-position--relative`);
        }

        // Place the datepicker flyout in the proper DOM location (after the trigger).
        $thisInput.siblings('.datepicker-icon-box').after(dp.$flyout.detach().hide()[0]);

        // The magic happens here.
        if ($.effects && $.effects[showAnim]) {
          dp.$flyout.show(showAnim, datepicker$1.get(dp, 'showOptions'), duration, postProcess);
        } else {
          dp.$flyout[showAnim](duration, postProcess);
        }
        if (duration === '') {
          postProcess();
        }
        datepicker$1.currentInstance = dp;
      }
    },
    // Generate the datepicker content.
    updateDatepicker,
    // Hides the datepicker from view.
    closeDateFlyout() {
      const dp = this.currentInstance;
      if (!dp) {
        return;
      }
      dp.hideInstance = true;
      if (dp.stayOpen) {
        this.selectDate(`#${dp.id}`, this.formatThisDate(dp, dp.currentDay, dp.currentMonth, dp.currentYear));
      }
      dp.stayOpen = false;
      if (this.flyoutIsOpen) {
        const duration = this.get(dp, 'duration');
        const showAnim = this.get(dp, 'showAnim');
        const postProcess = () => {
          dp.$flyout.attr('aria-hidden', 'true');

          // Prevent multiple DP focus events.
          setTimeout(() => {
            dp.hideInstance = false;
          }, 500);
        };
        if (duration && $.effects && $.effects[showAnim]) {
          dp.$flyout.hide(showAnim, datepicker$1.get(dp, 'showOptions'), duration, postProcess);
        } else {
          dp.$flyout[!duration ? 'hide' : showAnim === 'slideDown' ? 'slideUp' : showAnim === 'fadeIn' ? 'fadeOut' : 'hide'](duration, postProcess);
        }

        // Set the focus back to the input unless it's already on another trigger.
        const {
          classList
        } = document.activeElement;
        const $triggerButton = dp.$input.siblings(`.${this.triggerClass}`);
        if (!classList.contains(this.triggerClass) && !classList.contains(HELP_TRIGGER_CLASS)) {
          $triggerButton.focus();
        }
        const onClose = this.get(dp, 'onClose');
        const onChange = this.get(dp, 'onChange');
        if (onChange && dp.$input.val() !== dp.prevVal) {
          // Trigger custom callback.
          onChange.call(dp.$input.length ? dp.$input[0] : null, dp.$input.val());
        }
        if (onClose) {
          // Trigger custom callback.
          onClose.call(dp.$input.length ? dp.$input[0] : null, dp.$input.length ? dp.$input.val() : '', dp);
        }
        this.flyoutIsOpen = false;
        this.lastInput = null;
        $(`.${this.triggerClass}`).attr('aria-expanded', 'false');

        // Prevent multiple DP focus events.
        setTimeout(() => {
          dp.hideInstance = false;
        }, 1000);
      }
      this.currentInstance = null;
    },
    // Close datepicker if clicked elsewhere.
    checkExternalClick(event) {
      if (!datepicker$1.currentInstance) {
        return;
      }
      const $target = $(event.target);
      if (datepicker$1.flyoutIsOpen && !$target.parents(`#${datepicker$1.flyoutId}`).length && !$target.hasClass(datepicker$1.markerClassName) && !$target.hasClass(datepicker$1.triggerClass) && !$target.parents(`.${datepicker$1.triggerClass}`).length) {
        const pageOrModalScrollbarClick = event.target === $('html').get(0) || event.target === $('.spa-card__body').get(0);
        const modalIsActive = document.body.classList.contains('spa-ui-freeze-scroll');
        const positionIsRelative = datepicker$1.$flyout.eq(0).hasClass(`${FLYOUT_CLASS}-position--relative`);
        if (!pageOrModalScrollbarClick && !modalIsActive && !positionIsRelative) {
          datepicker$1.closeDateFlyout();
        }
      }
    },
    // Adjust one of the date sub-fields.
    adjustDate(selector, offset, period) {
      const $target = $(selector);
      const dp = this.getInstance($target[0]);
      if (this.isDisabledDatepicker($target[0])) {
        return;
      }
      this.adjustInstanceDate(dp, offset + (
      // Undo positioning.
      period === 'M' ? this.get(dp, 'showCurrentAtPos') : 0), period);
      $target.data('monthadjusted', offset);
      this.updateDatepicker(dp);

      // Since the flyout is repainted with a new month, make the first available day focusable.
      const $grid = dp.$flyout.find('table');
      const $td = $grid.find('[data-selectable]').first();
      $td.find('button').attr('tabindex', 0);
      $grid.attr('aria-activedescendant', $td.attr('id'));
    },
    // Action for selecting a new month/year.
    selectMonthYear(selector, select, period) {
      const $target = $(selector);
      const dp = this.getInstance($target[0]);
      dp.selectingMonthYear = false;
      const drawMonthOrYear = `draw${period === 'M' ? 'Month' : 'Year'}`;
      dp[drawMonthOrYear] = parseInt(select.options[select.selectedIndex].value, 10);
      dp[`selected${period === 'M' ? 'Month' : 'Year'}`] = dp[drawMonthOrYear];
      this.notifyChange(dp);
      this.adjustDate($target);
    },
    // Action for selecting a day.
    selectDay(selector, month, year, td, pastDue) {
      const $target = $(selector);
      if ($(td).hasClass(this.unselectableClass) || this.isDisabledDatepicker($target[0])) {
        return;
      }
      const dp = this.getInstance($target[0]);
      const $dateButton = $('button', td);
      dp.currentDay = $dateButton.find('span').html();
      dp.selectedDay = dp.currentDay;
      dp.currentMonth = month;
      dp.selectedMonth = dp.currentMonth;
      dp.currentYear = year;
      dp.selectedYear = dp.currentYear;
      if (dp.stayOpen) {
        dp.endDay = null;
        dp.endMonth = null;
        dp.endYear = null;
      }
      this.selectDate(selector, this.formatThisDate(dp, dp.currentDay, dp.currentMonth, dp.currentYear), pastDue);
      if (dp.stayOpen) {
        dp.rangeStart = this.daylightSavingAdjust(new Date(dp.currentYear, dp.currentMonth, dp.currentDay));
        this.updateDatepicker(dp);
      }
    },
    triggerDateChangeEvent(dp, pastDue) {
      const event = new Event(`change.${PROP_NAME}`);
      if (dp.$input.length) {
        dp.$input[0].dispatchEvent(event, dp.$input.val());

        // Publish sparta event on selecting a date.
        spartaEventsUtility.pub(`spartaDatePickerUtility.selectedDate_Callback`, {
          dp,
          pastDue
        });
      }
    },
    // Update the input field with the selected date.
    selectDate(selector, dateText, pastDue) {
      const $target = $(selector);
      const dp = this.getInstance($target[0]);
      if (!dateText) {
        dateText = this.formatThisDate(dp);
      }
      if (dp.$input) {
        dp.$input.val(dateText);
        this.triggerDateChangeEvent(dp, pastDue);
      }
      this.updateAlternate(dp);
      const onSelect = this.get(dp, 'onSelect');
      if (onSelect) {
        // Trigger custom callback.
        onSelect.call(dp.$input.length ? dp.$input[0] : null, dateText, dp);
      } else if (dp.$input) {
        dp.$input.trigger('change'); // Fire the change event.
      }
      if (dp.inline) {
        this.updateDatepicker(dp);
      } else if (!dp.stayOpen) {
        this.closeDateFlyout();
        [this.lastInput] = dp.$input;
        if (typeof this.lastInput === 'object') {
          this.lastInput.focus(); // Restore focus.
        } else {
          // Focus on the calendar icon.
          const nextInput = dp.$input.next();
          nextInput.focus();
        }
        this.lastInput = null;
      }

      // Allow DP to work with placeholder util, remove `placeholder` class when setting date.
      $target.removeClass('placeholder');
    },
    // Update any alternate field to synchronize with the main field.
    updateAlternate(dp) {
      const altField = this.get(dp, 'altField');
      if (altField) {
        // Update alternate field too.
        const altFormat = this.get(dp, 'altFormat') || this.get(dp, 'dateFormat');
        const date = this.getDate(dp);
        const dateText = this.formatDate(altFormat, date);
        $(altField).each(function eachAltField() {
          $(this).val(dateText);
        });
      }
    },
    /* Set as beforeShowDay function to prevent selection of weekends.
      @param  date  Date - the date to customise
      @return [boolean, string] - is this date selectable?, what is its CSS class? */
    noWeekends(date) {
      const day = date.getDay();
      return [day > 0 && day < 6, '']; // blez
    },
    /* Set as calculateWeek to determine the week of the year based on the ISO 8601 definition.
      @param  date  Date - the date to get the week for
      @return  number - the number of the week within the year that contains this date */
    iso8601Week(date) {
      const checkDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
      const firstMon = new Date(checkDate.getFullYear(), 1 - 1, 4); // First week always contains 1/4.
      let firstDay = firstMon.getDay() || 7; // Day of week: Mon = 1, ..., Sun = 7
      firstMon.setDate(firstMon.getDate() + 1 - firstDay); // Preceding Monday
      if (firstDay < 4 && checkDate < firstMon) {
        // Adjust first 3 days in year if necessary
        checkDate.setDate(checkDate.getDate() - 3); // Generate for previous year
        return datepicker$1.iso8601Week(checkDate);
      }
      if (checkDate > new Date(checkDate.getFullYear(), 12 - 1, 28)) {
        // Check last 3 days in year
        firstDay = new Date(checkDate.getFullYear() + 1, 1 - 1, 4).getDay() || 7;
        if (firstDay > 4 && (checkDate.getDay() || 7) < firstDay - 3) {
          // Adjust if necessary
          return 1;
        }
      }
      return Math.floor((checkDate - firstMon) / 86400000 / 7) + 1; // Weeks to given date
    },
    /* Parse a string value into a date object. See formatDate below for the possible formats.
      @param  format    string - the expected format of the date
      @param  value     string - the date in the above format
      @param  settings  Object - attributes include:
        shortYearCutoff  number - the cutoff year for determining the century (optional)
      @return  Date - the extracted date value or null if value is blank */
    parseDate(format, value, settings) {
      if (!format || !value) {
        throw new Error('Invalid arguments');
      }
      value = typeof value === 'object' ? value.toString() : `${value}`;
      if (!value) {
        return null;
      }
      let shortYearCutoff = settings ? settings.shortYearCutoff : null;
      if (!shortYearCutoff) {
        shortYearCutoff = this.defaults.shortYearCutoff;
      }
      const {
        monthNames,
        monthNamesShort,
        dayNames,
        dayNamesShort
      } = uiText[datepicker$1.language];
      let year = -1;
      let month = -1;
      let day = -1;
      let doy = -1;
      let literal = false;

      // Check whether a format character is doubled.
      let formatNum = 0;
      const lookAhead = match => {
        const matches = formatNum + 1 < format.length && format.charAt(formatNum + 1) === match;
        if (matches) {
          formatNum += 1;
        }
        return matches;
      };

      // Extract a number from the string value.
      let valueNum = 0;
      const getNumber = match => {
        lookAhead(match);
        const origSize = match === '@' ? 14 : match === 'y' ? 4 : match === 'o' ? 3 : 2;
        let size = origSize;
        let num = 0;
        while (size > 0 && valueNum < value.length && value.charAt(valueNum) >= '0' && value.charAt(valueNum) <= '9') {
          num = num * 10 + parseInt(value.charAt(valueNum), 10);
          valueNum += 1;
          size -= 1;
        }
        if (size === origSize) {
          throw new Error(`Missing number at position ${valueNum}`);
        }
        return num;
      };

      // Extract a name from the string value and convert to an index.
      const getName = (match, shortNames, longNames) => {
        const names = lookAhead(match) ? longNames : shortNames;
        let size = 0;
        for (let j = 0; j < names.length; j++) {
          size = Math.max(size, names[j].length);
        }
        let name = '';
        const initNum = valueNum;
        while (size > 0 && valueNum < value.length) {
          name += value.charAt(valueNum);
          valueNum += 1;
          for (let i = 0; i < names.length; i++) {
            if (name === names[i]) {
              return i + 1;
            }
          }
          size -= 1;
        }
        throw new Error(`Unknown name at position ${initNum}`);
      };

      // Confirm that a literal character matches the string value.
      const checkLiteral = () => {
        if (value.charAt(valueNum) !== format.charAt(formatNum)) {
          throw new Error(`Unexpected literal at position ${valueNum}`);
        }
        valueNum += 1;
      };
      for (formatNum = 0; formatNum < format.length; formatNum++) {
        if (literal) {
          if (format.charAt(formatNum) === "'" && !lookAhead("'")) {
            literal = false;
          } else {
            checkLiteral();
          }
        } else {
          switch (format.charAt(formatNum)) {
            case 'd':
              day = getNumber('d');
              break;
            case 'D':
              getName('D', dayNamesShort, dayNames);
              break;
            case 'o':
              doy = getNumber('o');
              break;
            case 'm':
              month = getNumber('m');
              break;
            case 'M':
              month = getName('M', monthNamesShort, monthNames);
              break;
            case 'y':
              year = getNumber('y');
              break;
            case '@':
              {
                const date = new Date(getNumber('@'));
                year = date.getFullYear();
                month = date.getMonth() + 1;
                day = date.getDate();
                break;
              }
            case "'":
              if (lookAhead("'")) {
                checkLiteral();
              } else {
                literal = true;
              }
              break;
            default:
              checkLiteral();
          }
        }
      }
      if (year === -1) {
        year = new Date().getFullYear();
      } else if (year < 100) {
        const fullYear = new Date().getFullYear();
        year += fullYear - fullYear % 100 + (year <= shortYearCutoff ? 0 : -100);
      }
      if (doy > -1) {
        month = 1;
        day = doy;
        let dim = -1;
        while (day > dim) {
          dim = this.getDaysInMonth(year, month - 1);
          if (day > dim) {
            month += 1;
            day -= dim;
          }
        }
      }
      const date = this.daylightSavingAdjust(new Date(year, month - 1, day));
      if (date.getFullYear() !== year || date.getMonth() + 1 !== month || date.getDate() !== day) {
        throw new Error('Invalid date'); // E.g. 31/02/*
      }
      return date;
    },
    // Standard date formats.
    ATOM: 'yy-mm-dd',
    // RFC 3339 (ISO 8601)
    COOKIE: 'D, dd M yy',
    ISO_8601: 'yy-mm-dd',
    RFC_822: 'D, d M y',
    RFC_850: 'DD, dd-M-y',
    RFC_1036: 'D, d M y',
    RFC_1123: 'D, d M yy',
    RFC_2822: 'D, d M yy',
    RSS: 'D, d M y',
    // RFC 822
    TIMESTAMP: '@',
    W3C: 'yy-mm-dd',
    // ISO 8601

    /* Format a date object into a string value. The format can be combinations of the following:
      d  - day of month (no leading zero)
      dd - day of month (two digit)
      o  - day of year (no leading zeros)
      oo - day of year (three digit)
      D  - day name short
      DD - day name long
      m  - month of year (no leading zero)
      mm - month of year (two digit)
      M  - month name short
      MM - month name long
      y  - year (two digit)
      yy - year (four digit)
      @ - Unix timestamp (ms since 01/01/1970)
      '...' - literal text
      '' - single quote
        @param  format    string - the desired format of the date
      @param  date      Date - the date value to format
      @return  string - the date in the above format */
    formatDate(format, date) {
      if (!date) {
        return '';
      }
      const {
        monthNames,
        monthNamesShort,
        dayNames,
        dayNamesShort
      } = uiText[datepicker$1.language];

      // Check whether a format character is doubled.
      let formatNum = 0;
      const lookAhead = match => {
        const matches = formatNum + 1 < format.length && format.charAt(formatNum + 1) === match;
        if (matches) {
          formatNum += 1;
        }
        return matches;
      };

      // Format a number, with leading zero if necessary.
      const formatNumber = (match, value, len) => {
        let num = `${value}`;
        if (lookAhead(match)) {
          while (num.length < len) {
            num = `0${num}`;
          }
        }
        return num;
      };

      // Format a name, short or long as requested.
      const formatName = (match, value, shortNames, longNames) => lookAhead(match) ? longNames[value] : shortNames[value];
      let output = '';
      let literal = false;
      if (date) {
        for (formatNum = 0; formatNum < format.length; formatNum++) {
          if (literal) {
            if (format.charAt(formatNum) === "'" && !lookAhead("'")) {
              literal = false;
            } else {
              output = `${output}${format.charAt(formatNum)}`;
            }
          } else {
            switch (format.charAt(formatNum)) {
              case 'd':
                output = `${output}${formatNumber('d', date.getDate(), 2)}`;
                break;
              case 'D':
                output = `${output}${formatName('D', date.getDay(), dayNamesShort, dayNames)}`;
                break;
              case 'o':
                {
                  let doy = date.getDate();
                  for (let m = date.getMonth() - 1; m >= 0; m -= 1) {
                    doy += this.getDaysInMonth(date.getFullYear(), m);
                  }
                  output = `${output}${formatNumber('o', doy, 3)}`;
                  break;
                }
              case 'm':
                output = `${output}${formatNumber('m', date.getMonth() + 1, 2)}`;
                break;
              case 'M':
                output = `${output}${formatName('M', date.getMonth(), monthNamesShort, monthNames)}`;
                break;
              case 'y':
                {
                  const formattedYear = lookAhead('y') ? date.getFullYear() : `${date.getYear() % 100 < 10 ? '0' : ''}${date.getYear() % 100}`;
                  output = `${output}${formattedYear}`;
                  break;
                }
              case '@':
                output = `${output}${date.getTime()}`;
                break;
              case "'":
                if (lookAhead("'")) {
                  output = `${output}'`;
                } else {
                  literal = true;
                }
                break;
              default:
                output = `${output}${format.charAt(formatNum)}`;
            }
          }
        }
      }
      return output;
    },
    // Get a setting value, defaulting if necessary.
    get(dp, name) {
      if (!dp || !dp.settings) {
        return null;
      }
      return typeof dp.settings[name] !== 'undefined' ? dp.settings[name] : this.defaults[name];
    },
    // Parse existing date and initialize datepicker.
    setDateFromField(dp) {
      const dateFormat = this.get(dp, 'dateFormat');
      const dateText = dp.$input ? dp.$input.val() : null;
      dp.endDay = null;
      dp.endMonth = null;
      dp.endYear = null;
      const settings = this.getFormatConfig(dp);
      const defaultDate = this.getDefaultDate(dp);
      const datepickerType = this.get(dp, 'datepickerType').toLocaleLowerCase();
      let date = null;
      try {
        date = this.parseDate(dateFormat, dateText, settings);
        // Determine if user has typed in a valid date or chose one from datepicker.
        if (date) {
          dp.isDateSelected = true;
        } else {
          date = defaultDate;
          dp.isDateSelected = false;
        }
      } catch (event) {
        this.log(event);
        dp.isDateSelected = false;
        date = defaultDate;
      }

      // TODO: This check for invalid date text should be improved.
      const nonDate = dateText === 'mm/dd/yyyy' || dateText === 'mm/dd/aaaa' || !dateText;

      // dp.selectedDay = nonDate ? 0 : date.getDate();
      dp.selectedDay = date.getDate();
      dp.selectedMonth = nonDate ? 0 : date.getMonth();
      dp.selectedYear = nonDate ? 0 : date.getFullYear();
      dp.drawMonth = date.getMonth();
      dp.drawYear = date.getFullYear();
      dp.currentDay = date.getDate();
      dp.currentMonth = date.getMonth();
      dp.currentYear = date.getFullYear();
      this.adjustInstanceDate(dp);

      // If the datepicker type is "range," set min/max date limits on the other input based on the
      // selection in the current input.
      if (datepickerType === 'range') {
        this.setRangeMinMaxLimits(dp);
      }
    },
    // Retrieve the default date shown on opening.
    getDefaultDate(dp) {
      let date = this.determineDate(this.get(dp, 'defaultDate'), new Date(), dp);
      const minDate = this.getMinMaxDate(dp, 'min', true);
      const maxDate = this.getMinMaxDate(dp, 'max');
      date = minDate && date < minDate ? minDate : date;
      date = maxDate && date > maxDate ? maxDate : date;
      return date;
    },
    // A date may be specified as an exact value or a relative one.
    determineDate(date, defaultDate, inst) {
      const offsetNumeric = offset => {
        const thisDate = new Date();
        thisDate.setDate(thisDate.getDate() + offset);
        return thisDate;
      };
      const offsetString = (offset, getDaysInMonth) => {
        try {
          return datepicker$1.parseDate(datepicker$1.get(inst, "dateFormat"), offset, datepicker$1.getFormatConfig(inst));
        } catch (e) {}
        const thisDate = new Date();
        let year = thisDate.getFullYear();
        let month = thisDate.getMonth();
        let day = thisDate.getDate();
        const pattern = /([+-]?[0-9]+)\s*(d|D|w|W|m|M|y|Y)?/g;
        let matches = pattern.exec(offset);
        while (matches) {
          switch (matches[2] || 'd') {
            case 'd':
            case 'D':
              day += parseInt(matches[1], 10);
              break;
            case 'w':
            case 'W':
              day += parseInt(matches[1], 10) * 7;
              break;
            case 'm':
            case 'M':
              month += parseInt(matches[1], 10);
              day = Math.min(day, getDaysInMonth(year, month));
              break;
            case 'y':
            case 'Y':
              year += parseInt(matches[1], 10);
              day = Math.min(day, getDaysInMonth(year, month));
              break;
          }
          matches = pattern.exec(offset);
        }
        return new Date(year, month, day);
      };
      date = !date ? defaultDate : typeof date === 'string' ? offsetString(date, this.getDaysInMonth) : typeof date === 'number' ? Number.isNaN(date) ? defaultDate : offsetNumeric(date) : date;
      date = date && (date.toString() === 'Invalid Date' ? defaultDate : date);
      if (date) {
        date.setHours(0);
        date.setMinutes(0);
        date.setSeconds(0);
        date.setMilliseconds(0);
      }
      return this.daylightSavingAdjust(date);
    },
    /* Handle switch to/from daylight saving. Hours may be non-zero on daylight saving cut-over: > 12
      when midnight changeover, but then cannot generate midnight datetime, so jump to 1AM, or reset.
      @param  date  (Date) the date to check
      @return  (Date) the corrected date */
    daylightSavingAdjust(date) {
      if (!date) {
        return null;
      }
      date.setHours(date.getHours() > 12 ? date.getHours() + 2 : 0);
      return date;
    },
    // Set the date(s) directly.
    setDate(dp, date) {
      const clear = !date;
      const origMonth = dp.selectedMonth;
      const origYear = dp.selectedYear;
      date = this.determineDate(date, new Date(), dp);
      dp.currentDay = date.getDate();
      dp.selectedDay = dp.currentDay;
      dp.currentMonth = date.getMonth();
      dp.selectedMonth = dp.currentMonth;
      dp.drawMonth = dp.currentMonth;
      dp.currentYear = date.getFullYear();
      dp.selectedYear = dp.currentYear;
      dp.drawYear = dp.currentYear;
      if (origMonth !== dp.selectedMonth || origYear !== dp.selectedYear) {
        this.notifyChange(dp);
      }
      this.adjustInstanceDate(dp);
      if (dp.$input) {
        dp.$input.val(clear ? '' : this.formatThisDate(dp));
      }
    },
    // Retrieve the date(s) directly.
    getDate(dp) {
      const startDate = !dp.currentYear || (dp.$input && !dp.$input.val() ? null : this.daylightSavingAdjust(new Date(dp.currentYear, dp.currentMonth - 1, dp.currentDay)));
      return startDate;
    },
    // Generate the HTML for the current state of the datepicker.
    generateHtml,
    // Generate the month and year header.
    generateMonthYearHeader(dp, drawMonth, drawYear, minDate, maxDate, selectedDate, secondary, monthNames, monthNamesShort) {
      minDate = dp.rangeStart && minDate && selectedDate < minDate ? selectedDate : minDate;
      const changeMonth = this.get(dp, 'changeMonth');
      const changeYear = this.get(dp, 'changeYear');
      const showMonthAfterYear = this.get(dp, 'showMonthAfterYear');
      const selectsClass = changeYear && changeMonth ? `${FLYOUT_CLASS}-title--selects` : '';
      const {
        selectMonth,
        selectYear
      } = uiText[datepicker$1.language];
      let html = `
        <div
          id="${this.$flyout[0].id}_label"
          aria-live="polite"
          class="${FLYOUT_CLASS}-title ${selectsClass}"
        >
      `;
      let monthHtml;
      const months = window.innerWidth < 375 ? monthNamesShort : monthNames;
      if (secondary || !changeMonth) {
        monthHtml = `
          <span class="${FLYOUT_CLASS}-month">${months[drawMonth]}</span>
        `;
      } else {
        const inMinYear = minDate && minDate.getFullYear() === drawYear;
        const inMaxYear = maxDate && maxDate.getFullYear() === drawYear;
        monthHtml = `
          <div class="spa-input spa-input-box spa-dropdown spa-input--sparta2">
            <select
              id="dropdownMonthSelect"
              name="dropdownMonthSelect"
              class="spa-input-select ${FLYOUT_CLASS}-month"
              aria-label="${selectMonth}"
            >
        `;
        for (let month = 0; month < 12; month++) {
          if ((!inMinYear || month >= minDate.getMonth()) && (!inMaxYear || month <= maxDate.getMonth())) {
            monthHtml = `
              ${monthHtml}
                  <option value="${month}"${month === drawMonth ? ' selected="selected"' : ''}>
                    ${months[month]}
                  </option>
            `;
          }
        }
        monthHtml = `
          ${monthHtml}
            </select>
          </div>
        `;
      }
      if (!showMonthAfterYear) {
        const spaceIfNeeded = (secondary || changeMonth || changeYear) && !(changeMonth && changeYear) ? '&nbsp;' : '';
        html = `
          ${html}
            ${monthHtml}
            ${spaceIfNeeded}
        `;
      }

      // Year selection.
      if (secondary || !changeYear) {
        html = `
          ${html}
            <span class="${FLYOUT_CLASS}-year">${drawYear}</span>
        `;
      } else {
        // Determine range of years to display.
        const years = this.get(dp, 'yearRange').split(':');
        let year = 0;
        let endYear = 0;
        if (years.length !== 2) {
          year = drawYear - 10;
          endYear = drawYear + 10;
        } else if (years[0].charAt(0) === '+' || years[0].charAt(0) === '-') {
          year = drawYear + parseInt(years[0], 10);
          endYear = drawYear + parseInt(years[1], 10);
        } else {
          year = parseInt(years[0], 10);
          endYear = parseInt(years[1], 10);
        }
        year = minDate ? Math.max(year, minDate.getFullYear()) : year;
        endYear = maxDate ? Math.min(endYear, maxDate.getFullYear()) : endYear;
        html = `
          ${html}
            <div class="spa-input spa-input-box spa-dropdown spa-input--sparta2">
              <select
                id="dropdownYearSelect"
                name="dropdownYearSelect"
                class="spa-input-select ${FLYOUT_CLASS}-year"
                aria-label="${selectYear}"
              >
        `;
        for (; year <= endYear; year++) {
          html = `
            ${html}
                  <option value="${year}"${year === drawYear ? ' selected="selected"' : ''}>
                    ${year}
                  </option>
          `;
        }
        html = `
          ${html}
              </select>
            </div>
        `;
      }
      if (showMonthAfterYear) {
        const spaceIfNeeded = secondary || changeMonth || changeYear ? '&nbsp;' : '';
        html = `
          ${html}
            ${spaceIfNeeded}
            ${monthHtml}
        `;
      }

      // Close the datepicker title header.
      html = `
        ${html}
        </div>
      `;
      return html;
    },
    // Adjust one of the date sub-fields.
    adjustInstanceDate(dp, offset, period) {
      const year = period === 'Y' ? dp.drawYear + offset : dp.drawYear;
      const month = period === 'M' ? dp.drawMonth + offset : dp.drawMonth;
      let day = Math.min(dp.selectedDay, this.getDaysInMonth(year, month));
      if (period === 'D') {
        day += offset;
      }
      let date = this.daylightSavingAdjust(new Date(year, month, day));

      // Ensure the date is within the bounds set.
      const minDate = this.getMinMaxDate(dp, 'min', true);
      const maxDate = this.getMinMaxDate(dp, 'max');
      date = minDate && date < minDate ? minDate : date;
      date = maxDate && date > maxDate ? maxDate : date;
      dp.selectedDay = date.getDate();
      dp.selectedMonth = date.getMonth();
      dp.drawMonth = dp.selectedMonth;
      dp.selectedYear = date.getFullYear();
      dp.drawYear = dp.selectedYear;
      if (period === 'M' || period === 'Y') {
        this.notifyChange(dp);
      }
    },
    // Notify change of month/year.
    notifyChange(dp) {
      const onChangeMonthYear = this.get(dp, 'onChangeMonthYear');
      if (onChangeMonthYear) {
        onChangeMonthYear.call(dp.$input.length ? dp.$input[0] : null, dp.selectedYear, dp.selectedMonth + 1, dp);
      }
    },
    // Determine the number of months to show.
    getNumberOfMonths(dp) {
      const numMonths = this.get(dp, 'numberOfMonths');
      return !numMonths ? [1, 1] : typeof numMonths === 'number' ? [1, numMonths] : numMonths;
    },
    // Determine the current maximum date. Ensure no time components are set.
    // May be overridden for a range.
    getMinMaxDate(dp, minMax, checkRange) {
      const date = this.determineDate(this.get(dp, `${minMax}Date`), null, dp);
      return !checkRange || !dp.rangeStart ? date : !date || dp.rangeStart > date ? dp.rangeStart : date;
    },
    // Find the number of days in a given month.
    getDaysInMonth(year, month) {
      return 32 - new Date(year, month, 32).getDate();
    },
    // Find the day of the week of the first of a month.
    getFirstDayOfMonth(year, month) {
      return new Date(year, month, 1).getDay();
    },
    // Determines if we should allow a "next/prev" month display change.
    canAdjustMonth(dp, offset, curYear, curMonth) {
      const numMonths = this.getNumberOfMonths(dp);
      const date = this.daylightSavingAdjust(new Date(curYear, curMonth + (offset < 0 ? offset : numMonths[1]), 1));
      if (offset < 0) {
        date.setDate(this.getDaysInMonth(date.getFullYear(), date.getMonth()));
      }
      return this.isInRange(dp, date);
    },
    // Is the given date in the accepted range?
    isInRange(dp, date) {
      // during range selection, use minimum of selected date and range start
      let newMinDate = !dp.rangeStart ? null : this.daylightSavingAdjust(new Date(dp.selectedYear, dp.selectedMonth, dp.selectedDay));
      newMinDate = newMinDate && dp.rangeStart < newMinDate ? dp.rangeStart : newMinDate;
      const minDate = newMinDate || this.getMinMaxDate(dp, 'min');
      const maxDate = this.getMinMaxDate(dp, 'max');
      return (!minDate || date >= minDate) && (!maxDate || date <= maxDate);
    },
    // Provide the configuration settings for formatting/parsing.
    getFormatConfig(dp) {
      let shortYearCutoff = this.get(dp, 'shortYearCutoff');
      shortYearCutoff = typeof shortYearCutoff !== 'string' ? shortYearCutoff : new Date().getFullYear() % 100 + parseInt(shortYearCutoff, 10);
      return {
        shortYearCutoff
      };
    },
    // Format the given date for display.
    formatThisDate(dp, day, month, year) {
      if (!day) {
        dp.currentDay = dp.selectedDay;
        dp.currentMonth = dp.selectedMonth;
        dp.currentYear = dp.selectedYear;
      }
      const date = day ? typeof day === 'object' ? day : this.daylightSavingAdjust(new Date(year, month, day)) : this.daylightSavingAdjust(new Date(dp.currentYear, dp.currentMonth, dp.currentDay));
      return this.formatDate(this.get(dp, 'dateFormat'), date);
    },
    setRangeMinMaxLimits(dp) {
      // The term "partner" here represents the other end of current range instance.
      // If the current instance is for "start date," then the other instance must be for "end date"
      // and vice-versa.
      const partner = this.identifyRangePartner(dp);
      const partnerInstance = this.getInstance($(partner.selector)[0]);

      // memoize initial min/max date so you can restore when date inputs are empty
      partnerInstance.settings.initialMinDate = partnerInstance.settings.initialMinDate || partnerInstance.settings.minDate;
      partnerInstance.settings.initialMaxDate = partnerInstance.settings.initialMaxDate || partnerInstance.settings.maxDate;
      if (partner.type === 'end' && dp.isDateSelected) {
        // setting minDate limit in "end date" instance based on
        // the selection of current "start date" instance
        partnerInstance.settings.minDate = new Date(dp.selectedYear, dp.selectedMonth, dp.selectedDay, 0, 0, 0, 0);
      } else {
        // restore minDate with initial value if current instance doesn't have valid date
        partnerInstance.settings.minDate = partnerInstance.settings.initialMinDate;
      }
      if (partner.type === 'start' && dp.isDateSelected) {
        // setting maxDate limit in "start date" instance based on
        // the selection of current "end date" instance
        partnerInstance.settings.maxDate = new Date(dp.selectedYear, dp.selectedMonth, dp.selectedDay, 0, 0, 0, 0);
      } else {
        // restore maxDate with initial value if current instance doesn't have valid date
        partnerInstance.settings.maxDate = partnerInstance.settings.initialMaxDate;
      }
    },
    identifyRangePartner(dp) {
      // current instance is "start date", return "end date"
      if (dp.settings.selector === dp.settings.rangeStartSelector) {
        return {
          type: 'end',
          selector: dp.settings.rangeEndSelector
        };
      }

      // current instance is "end date", return "start date"
      if (dp.settings.selector === dp.settings.rangeEndSelector) {
        return {
          type: 'start',
          selector: dp.settings.rangeStartSelector
        };
      }
      return null;
    }
  });

  /* Invoke the datepicker functionality.
    @param  options  string - a command, optionally followed by additional parameters or
                      Object - settings for attaching new datepicker functionality
    @return  jQuery object */
  function initDatepicker(options, ...otherArgs) {
    // Initialize the datepicker.
    if (!datepicker$1.initialized) {
      $(document).mousedown(datepicker$1.checkExternalClick);

      // Throw datepicker into the DOM so all the event bindings can be initialized onto it.
      this.after(datepicker$1.$flyout);
      datepicker$1.initialized = true;
    }
    if (typeof options === 'string' && (options === 'isDisabled' || options === 'getDate' || options === 'option' && otherArgs.length === 1 && typeof otherArgs[0] === 'string')) {
      return datepicker$1[`${options}Datepicker`](...[this[0]].concat(otherArgs));
    }
    return this.each(function eachInstance() {
      if (typeof options === 'string') {
        datepicker$1[`${options}Datepicker`](...[this].concat(otherArgs));
      } else {
        datepicker$1.attachDatepicker(this, options);
      }
    });
  }
  $.fn.datepicker = initDatepicker;
  datepicker$1 = new Datepicker(); // Singleton instance.
  datepicker$1.initialized = false;
  datepicker$1.uuid = new Date().getTime();
  datepicker$1.version = '1.7.2';
  return datepicker$1;
}

/* eslint-disable require-jsdoc */
function monthpicker (input, settings) {
  const datepicker = core();
  const PROP_NAME = 'datepicker';
  const FLYOUT_CLASS = 'ui-datepicker';
  const FLYOUT_PREV_CLASS = `${FLYOUT_CLASS}-prev`;
  const FLYOUT_NEXT_CLASS = `${FLYOUT_CLASS}-next`;
  const FLYOUT_CLOSE_CLASS = `${FLYOUT_CLASS}-close`;
  const STATE_CLASS = 'ui-state';
  const TOKEN_CLASS = 'ui-monthpicker';
  const HELP_TRIGGER_CLASS = 'toggle-trigger';
  const UI_TEXT = uiText[datepicker.language];
  const BLUR_DELAY = 100; // Wait a moment before reacting to a blur.
  const ITEMS_PER_ROW = 3;
  const options = {
    ...settings
  };
  const $input = $(input);
  let $flyout = null;
  function setSelectedDates() {
    const rawValue = $input.val();

    // Value may use a comma as a separator or just a space.
    const parts = rawValue.length > 0 ? rawValue.replace(', ', ' ').split(' ') : [];
    options.selectedDate = null;
    $input.val('');
    if (parts.length === 2) {
      const month = parts[0].trim();
      const year = parts[1].trim();
      const monthIndex = helpers.getMonthIndex(month, UI_TEXT);
      if (monthIndex > -1 && helpers.isFullYear(year)) {
        const selectedDate = new Date(year, monthIndex, 1);
        if (helpers.isDateInMinMaxRange(selectedDate, options)) {
          options.selectedDate = selectedDate;
          $input.val(`${UI_TEXT.monthNamesShort[selectedDate.getMonth()]} ${selectedDate.getFullYear()}`);
        }
      }
    }
  }
  function initOptions() {
    helpers.setupMinMaxDates(options);
    setSelectedDates();
    options.today = helpers.getToday();
    options.currentYearView = null;
  }
  function setMonthSelection(value) {
    const event = new Event(`change.${PROP_NAME}`);
    $input.val(value)[0].dispatchEvent(event, value);
  }
  function renderYearNavigation(initialDate) {
    const currentYear = initialDate.getFullYear();
    const isNextYearAvailable = options.maxDate.getTime() >= new Date(currentYear + 1, 0, 1).getTime();
    const isPrevYearAvailable = options.minDate.getTime() <= new Date(currentYear - 1, 11, 1).getTime();
    const prevClass = isPrevYearAvailable ? '' : `${FLYOUT_PREV_CLASS}--disabled`;
    const nextClass = isNextYearAvailable ? '' : `${FLYOUT_NEXT_CLASS}--disabled`;
    const fromYear = options.minDate.getFullYear();
    const toYear = options.maxDate.getFullYear();
    const selectedYear = initialDate.getFullYear();

    // Decide whether to show 1) dropdown or 2) previous/next year links.
    let markup;
    if (options.changeYear) {
      markup = `
        <div class="${FLYOUT_CLASS}-title ${FLYOUT_CLASS}-title--selects">
          <div class="spa-input spa-input-box spa-dropdown spa-input--sparta2">
            <select
              id="monthpickerDropdownYearSelect"
              name="monthpickerDropdownYearSelect"
              class="spa-input-select ${FLYOUT_CLASS}-year"
              aria-label="${UI_TEXT.selectYear}"
            >
      `;
      for (let year = fromYear; year <= toYear; year++) {
        markup = `
          ${markup}
                <option value="${year}" ${year === selectedYear ? 'selected' : ''}>${year}</option>
        `;
      }
      markup = `
        ${markup}
            </select>
          </div>
        </div>
      `;
    } else {
      markup = `
        <button
          type="button"
          class="${FLYOUT_PREV_CLASS} ${prevClass}"
          title="${UI_TEXT.prevYearText}"
          ${isPrevYearAvailable ? '' : 'disabled tabindex="-1"'}
        >&nbsp;&nbsp;
          <span class="ada-hidden">${UI_TEXT.prevYearView}</span>
        </button>
        <div class="${FLYOUT_CLASS}-title">
          <span class="${FLYOUT_CLASS}-year">${initialDate.getFullYear()}</span>
        </div>
        <button
          type="button"
          class="${FLYOUT_NEXT_CLASS} ${nextClass}"
          title="${UI_TEXT.nextYearText}"
          ${isNextYearAvailable ? '' : 'disabled tabindex="-1"'}
        >&nbsp;&nbsp;
          <span class="ada-hidden">${UI_TEXT.nextYearView}</span>
        </button>
      `;
    }
    return markup;
  }
  function closeMonthFlyout() {
    const {
      datepickerType,
      onChange,
      prevVal = ''
    } = options;
    if ($flyout.is(':visible')) {
      options.currentYearView = null;
      $flyout.hide('medium');
      const $triggerButton = $input.siblings(`.${datepicker.triggerClass}`);
      $triggerButton.attr('aria-expanded', 'false');

      // Set the focus back to the input unless it's already on another trigger.
      const {
        classList
      } = document.activeElement;
      if (!classList.contains(datepicker.triggerClass) && !classList.contains(HELP_TRIGGER_CLASS)) {
        $triggerButton.focus();
      }
      if (datepickerType.includes('month') && onChange && prevVal !== $input.val()) {
        // Trigger custom callback.
        onChange.call(options.input ? options.input[0] : null, $input.val());
      }
    }
  }
  datepicker.closeMonthFlyout = closeMonthFlyout;
  function closeWeekFlyout() {
    if (typeof datepicker.closeWeekFlyout === 'function') {
      datepicker.closeWeekFlyout();
    }
  }
  function closeDateFlyout() {
    if (typeof datepicker.closeDateFlyout === 'function') {
      datepicker.closeDateFlyout();
    }
  }

  // Closes the flyout only if the whole monthpicker has lost focus.
  function doBlur() {
    setTimeout(() => {
      const focusedEl = $(':focus');
      const focusOutside = focusedEl.length // Something is in focus.
      && !$flyout.closest('.spa-input').find(':focus').length // It's not here.
      ;
      if (focusOutside || focusedEl.hasClass(HELP_TRIGGER_CLASS)) {
        closeMonthFlyout();
      }
    }, BLUR_DELAY);
  }
  function renderFlyout() {
    const initialDate = options.currentYearView || options.selectedDate || options.today;
    const year = initialDate.getFullYear();
    const $triggerButton = $input.siblings(`.${datepicker.triggerClass}`);
    $triggerButton.attr('aria-expanded', 'true');
    options.currentYearView = new Date(year, 0, 1);

    // Header.
    let markup = `
      <div class="content-body" aria-modal="true" role="dialog">
        <div class="${FLYOUT_CLASS}-header ui-widget-header ui-helper-clearfix ui-corner-all">
          ${renderYearNavigation(initialDate)}
          <button
            type="button"
            title="${UI_TEXT.closeToolTipText}"
            class="${FLYOUT_CLOSE_CLASS}"
          >
            <span class="ada-hidden">${UI_TEXT.closeText}</span>
          </button>
        </div>
        <table class="${TOKEN_CLASS}-calendar" role="grid">
          <tbody>
    `;

    // Body.
    let hasSelected = false;
    for (let i = 0; i < 12; i++) {
      const currentDate = new Date(year, i, 1);
      const month = UI_TEXT.monthNames[i];
      if (i % 3 === 0) {
        markup = `
          ${markup}
                <tr>
        `;
      }
      markup = `
        ${markup}
                <td>
      `;
      if (helpers.isDateInMinMaxRange(currentDate, options)) {
        const isSelected = helpers.isValidDate(options.selectedDate) && helpers.isDateSame(currentDate, options.selectedDate);
        if (isSelected) {
          hasSelected = true;
        }
        const selectedMonthClass = isSelected ? `${TOKEN_CLASS}-item--selected` : '';
        markup = `
          ${markup}
                    <button
                      type="button"
                      class="${TOKEN_CLASS}-item ${selectedMonthClass}"
                      aria-label="${month}, ${year}"
                      title="${month}, ${year}"
                      tabindex="${isSelected ? 0 : -1}"
                      ${isSelected ? 'aria-selected="true"' : ''}
                    ><span aria-hidden="true">${UI_TEXT.monthNamesShort[i]}</span></button>
        `;
      } else {
        markup = `
          ${markup}
                    <span
                      class="${TOKEN_CLASS}-placeholder"
                    >
                      <span aria-hidden="true">${UI_TEXT.monthNamesShort[i]}</span>
                    </span>
        `;
      }
      markup = `
        ${markup}
                </td>
      `;
      if (i % 3 === 2) {
        markup = `
          ${markup}
                </tr>
        `;
      }
    }

    // Footer.
    markup = `
      ${markup}
          </tbody>
        </table>
        <div class="boa-ada-end ada-hidden">${UI_TEXT.endOfLayer}.</div>
      </div>
    `;
    $flyout.html(markup);

    // Ensure that one of the calendar items can receive focus.
    if (!hasSelected) {
      const $grid = $flyout.find(`.${TOKEN_CLASS}-calendar`);
      const $activeItem = $grid.find(`.${TOKEN_CLASS}-item`).first();
      $activeItem.attr('tabindex', 0);
      $grid.attr('aria-activedescendant', $activeItem.attr('id'));
    }

    // Activate the available calendar items.
    const availableMonths = $flyout.find(`.${TOKEN_CLASS}-calendar button`);
    availableMonths.on('click', e => {
      e.stopPropagation();
      const $target = $(e.target);
      setMonthSelection($target.attr('title') || $target.parent().attr('title'));
      setSelectedDates();
      $input.data(PROP_NAME, options);
      closeMonthFlyout();
      $input.focus();
    });

    // When the year is changed, re-render the flyout.
    $flyout.find(`.${FLYOUT_CLASS}-year`).on('change', e => {
      options.currentYearView = new Date($(e.target).val(), 0, 1);
      renderFlyout();

      // Restore focus after updating the flyout.
      $flyout.find(`.${FLYOUT_CLASS}-year`).focus();
    });

    // Allow any button in the flyout to close the monthpicker on blur.
    $flyout.find('button').on('blur', doBlur);
  }
  function showYear(current, revised) {
    const revisedYear = revised.getFullYear();
    if (revisedYear >= options.minDate.getFullYear() && revisedYear <= options.maxDate.getFullYear()) {
      options.currentYearView = revised;
      renderFlyout();
      return true;
    }
    options.currentYearView = current;
    return false;
  }
  function showPrevYear() {
    return showYear(options.currentYearView, new Date(options.currentYearView.getFullYear() - 1, 0, 1));
  }
  function showNextYear() {
    return showYear(options.currentYearView, new Date(options.currentYearView.getFullYear() + 1, 0, 1));
  }

  // Sets the focus to a given month (if specified) or the first focusable month.
  function selectMonth($item = null) {
    // Make all of the items unfocusable.
    const $grid = $item.closest(`.${TOKEN_CLASS}-calendar`);
    const $items = $grid.find(`.${TOKEN_CLASS}-item`);
    $items.attr('tabindex', -1);

    // Find a cell to select if none was supplied.
    let $viableCell = $item || $items.find(`.${STATE_CLASS}-active`);
    $viableCell = $viableCell.first();

    // Make only the selected month (or first viable month) focusable.
    $viableCell.attr('tabindex', 0).focus();
    $grid.attr('aria-activedescendant', $viableCell.attr('id'));
  }
  function monthFocus(flyout) {
    const $grid = flyout.find(`.${TOKEN_CLASS}-calendar`);
    const $selectedItem = $grid.find(`.${TOKEN_CLASS}-item--selected`).first();
    const $currMonth = $grid.find(`.${TOKEN_CLASS}-item`).eq(options.today.getMonth()).first();
    const $activeItem = $selectedItem.length ? $selectedItem : $currMonth;
    $grid.find(`.${TOKEN_CLASS}-item`).attr('tabindex', '-1');
    // Focus month after flyout is fully open.
    setTimeout(() => {
      $activeItem.attr('tabindex', '0').focus();
    }, 500);
  }
  function initHandlers() {
    // Get this calendar icon button and input tied to the monthpicker.
    const $triggerButton = $input.siblings(`.${datepicker.triggerClass}`);
    const mainDivId = `${datepicker.flyoutId}-${TOKEN_CLASS}`;
    function moveFocus(dir) {
      let yearChanged = false;

      // Determine which item should receive focus.
      let itemIndex = 0;
      let $monthItems = $flyout.find(`.${TOKEN_CLASS}-item`);
      let prevMonthCount = 0;
      const $focusedItem = $flyout.find(`.${TOKEN_CLASS}-item:focus`);
      if ($focusedItem.length) {
        itemIndex = $monthItems.index($focusedItem);
        if (dir === 'up') {
          if (itemIndex > ITEMS_PER_ROW - 1) {
            itemIndex -= ITEMS_PER_ROW;
          } else {
            yearChanged = showPrevYear();
          }
        } else if (dir === 'down') {
          if (itemIndex < $monthItems.length - ITEMS_PER_ROW) {
            itemIndex += ITEMS_PER_ROW;
          } else {
            prevMonthCount = $monthItems.length;
            yearChanged = showNextYear();
          }
        } else if (dir === 'left') {
          if (itemIndex > 0) {
            itemIndex -= 1;
          } else {
            yearChanged = showPrevYear();
          }
        } else if (dir === 'right') {
          if (itemIndex < $monthItems.length - 1) {
            itemIndex += 1;
          } else {
            yearChanged = showNextYear();
          }
        }
      }

      // If no viable cell was found and the year was changed, update references.
      if (yearChanged) {
        $monthItems = $flyout.find(`.${TOKEN_CLASS}-item`);
        if (dir === 'up') {
          itemIndex = itemIndex + $monthItems.length - ITEMS_PER_ROW;
        } else if (dir === 'down') {
          itemIndex = Math.min(itemIndex - prevMonthCount + ITEMS_PER_ROW, $monthItems.length - 1);
        } else if (dir === 'left') {
          itemIndex = $monthItems.length - 1;
        } else if (dir === 'right') {
          itemIndex = 0;
        }
      }

      // Move the focus to the appropriate item.
      if (typeof dir === 'undefined') {
        itemIndex = options.today.getMonth();
      }
      const $monthToFocus = $monthItems.eq(itemIndex);
      if ($monthToFocus) {
        selectMonth($monthToFocus);
        return;
      }

      // If no month was in focus, just focus on the first viable month.
      selectMonth();
    }
    function handleArrowDown(event) {
      let eventHandled = false;
      let moveFocusToGrid = false;
      if ($flyout.is(':hidden')) {
        // If it's not open already, open the flyout.
        $triggerButton.trigger('click');
        moveFocusToGrid = true;
      } else if (event.target.classList.contains(`${TOKEN_CLASS}-item`)) {
        moveFocus('down');
        eventHandled = true;
      } else {
        // If the down arrow was pressed on a flyout control, move the focus into the grid.
        moveFocusToGrid = true;
      }
      if (moveFocusToGrid) {
        let focusElement = $(`.${TOKEN_CLASS}-item--selected`);
        if (!focusElement.length) {
          focusElement = $(`button[tabindex=0]`);
        }
        focusElement.focus();
        eventHandled = true;
      }
      return eventHandled;
    }
    function handleEscape() {
      closeMonthFlyout();
      closeWeekFlyout();
    }
    function doKeyDown(event) {
      let eventHandled = false;
      const noAltShiftOrCtrl = !event.altKey && !event.shiftKey && !event.ctrlKey;
      if (event.key === 'Escape') {
        handleEscape();
        eventHandled = true;
      } else if (event.key === 'Tab') {
        helpers.focusTrap($flyout, event);
      } else if (noAltShiftOrCtrl && event.target.tagName.toUpperCase() !== 'SELECT') {
        if (event.key === 'ArrowUp' && event.target.classList.contains(`${TOKEN_CLASS}-item`)) {
          moveFocus('up');
          eventHandled = true;
        } else if (event.key === 'ArrowDown') {
          eventHandled = handleArrowDown(event);
        } else if (event.key === 'ArrowLeft') {
          moveFocus('left');
          eventHandled = true;
        } else if (event.key === 'ArrowRight') {
          moveFocus('right');
          eventHandled = true;
        }
      }
      if (eventHandled) {
        event.preventDefault();
        event.stopPropagation();
      }
    }
    $input.off('keydown').on('keydown', doKeyDown).off('blur').on('blur', doBlur);
    $triggerButton.attr('aria-controls', mainDivId).off('click').on('click', e => {
      e.stopPropagation();

      // Just close the flyout if it's already visible.
      if ($flyout.is(':visible')) {
        closeMonthFlyout();
        return;
      }

      // Close other flyouts if we're opening a new one.
      closeWeekFlyout();
      closeDateFlyout();
      $(`.${TOKEN_CLASS}`).not($flyout).hide('medium');

      // Open the flyout.
      options.prevVal = $input.val();
      setSelectedDates();
      renderFlyout();
      helpers.positionFlyout($flyout, $input);
      $flyout.show('medium');
      monthFocus($flyout);
    }).off('keydown').on('keydown', doKeyDown).off('blur').on('blur', doBlur);
    $flyout.off('click').on('click', event => {
      event.stopPropagation();
      const {
        classList
      } = event.target;
      if (classList.contains(FLYOUT_PREV_CLASS)) {
        showPrevYear();
        let $buttonToFocus = $flyout.find(`.${FLYOUT_PREV_CLASS}`);
        if ($buttonToFocus.is(':disabled')) {
          $buttonToFocus = $flyout.find(`.${FLYOUT_NEXT_CLASS}`);
        }
        $buttonToFocus.focus();
      } else if (classList.contains(FLYOUT_NEXT_CLASS)) {
        showNextYear();
        let $buttonToFocus = $flyout.find(`.${FLYOUT_NEXT_CLASS}`);
        if ($buttonToFocus.is(':disabled')) {
          $buttonToFocus = $flyout.find(`.${FLYOUT_PREV_CLASS}`);
        }
        $buttonToFocus.focus();
      } else if (classList.contains(FLYOUT_CLOSE_CLASS)) {
        closeMonthFlyout();
      }
    }).off('keydown').on('keydown', doKeyDown);
    $(document).on('click', () => {
      closeMonthFlyout();
      closeWeekFlyout();
    });
  }
  function init() {
    // If monthpicker is already attached to current input, do not proceed any further.
    if ($input.next().hasClass(TOKEN_CLASS)) {
      return;
    }
    const TEMPLATE = helpers.getFlyoutTemplate(TOKEN_CLASS);
    $flyout = $(TEMPLATE).insertAfter($input.siblings().last());
    initOptions();
    initHandlers();
  }
  init();
}

/* eslint-disable require-jsdoc */
function weekpicker (input, settings) {
  const datepicker = core();
  const PROP_NAME = 'datepicker';
  const FLYOUT_CLASS = 'ui-datepicker';
  const FLYOUT_PREV_CLASS = `${FLYOUT_CLASS}-prev`;
  const FLYOUT_NEXT_CLASS = `${FLYOUT_CLASS}-next`;
  const FLYOUT_CLOSE_CLASS = `${FLYOUT_CLASS}-close`;
  const STATE_CLASS = 'ui-state';
  const TOKEN_CLASS = 'ui-weekpicker';
  const HELP_TRIGGER_CLASS = 'toggle-trigger';
  const UI_TEXT = uiText[datepicker.language];
  const BLUR_DELAY = 100; // Wait a moment before reacting to a blur.

  const options = {
    ...settings
  };
  const $input = $(input);
  let $flyout = null;
  function setSelectedDates() {
    const rawValue = $input.val();
    const dates = rawValue.length > 0 ? rawValue.split('-') : [];
    options.selectedStartDate = null;
    options.selectedEndDate = null;
    input.val('');
    if (dates.length === 2) {
      const fromDate = new Date(dates[0].trim());
      if (helpers.isValidDate(fromDate) && helpers.isWeekStartDate(fromDate, options)) {
        options.selectedStartDate = fromDate;
        const toDate = new Date(dates[1].trim());
        const expectedToDate = helpers.cloneDate(fromDate);
        expectedToDate.setDate(fromDate.getDate() + (options.showWeekend === 1 ? 4 : 6));
        if (helpers.isValidDate(toDate) && helpers.isDateSame(toDate, expectedToDate)) {
          options.selectedEndDate = toDate;
          $input.val(helpers.getConciseWeekString(options.selectedStartDate, options.selectedEndDate));
        }
      }
    }
  }
  function setWeekSelection(value) {
    const event = new Event(`change.${PROP_NAME}`);
    $input.val(value)[0].dispatchEvent(event, value);
  }
  function closeWeekFlyout() {
    const {
      datepickerType,
      onChange,
      prevVal = ''
    } = options;
    if ($flyout.is(':visible')) {
      options.currentMonthView = null;
      $flyout.hide('medium');

      // Set the focus back to the input unless it's already on another trigger.
      const {
        classList
      } = document.activeElement;
      if (!classList.contains(datepicker.triggerClass) && !classList.contains(HELP_TRIGGER_CLASS)) {
        $input.focus();
      }
      if (datepickerType.includes('week') && onChange && prevVal !== $input.val()) {
        // Trigger custom callback.
        onChange.call(options.input ? options.input[0] : null, $input.val());
      }
    }
  }
  datepicker.closeWeekFlyout = closeWeekFlyout;
  function closeMonthFlyout() {
    if (typeof datepicker.closeMonthFlyout === 'function') {
      datepicker.closeMonthFlyout();
    }
  }
  function closeDateFlyout() {
    if (typeof datepicker.closeDateFlyout === 'function') {
      datepicker.closeDateFlyout();
    }
  }

  // Closes the flyout only if the whole weekpicker has lost focus.
  function doBlur() {
    setTimeout(() => {
      const focusedEl = $(':focus');
      const focusOutside = focusedEl.length // Something is in focus.
      && !$flyout.closest('.spa-input').find(':focus').length // It's not here.
      ;
      if (focusOutside || focusedEl.hasClass(HELP_TRIGGER_CLASS)) {
        closeWeekFlyout();
      }
    }, BLUR_DELAY);
  }
  function renderFlyout() {
    const {
      selectedStartDate,
      selectedEndDate,
      today,
      currentMonthView
    } = options;
    const initialDate = currentMonthView || selectedStartDate || today;
    const year = initialDate.getFullYear();
    const monthIndex = initialDate.getMonth();
    const startOfMonth = new Date(year, monthIndex, 1);
    const endOfMonth = new Date(year, monthIndex + 1, 0);
    const isNextMonthAvailable = options.maxDate.getTime() >= new Date(year, monthIndex + 1, 1).getTime();
    const isPrevMonthAvailable = options.minDate.getTime() <= new Date(year, monthIndex, 0).getTime();
    options.currentMonthView = new Date(year, monthIndex, 1);

    // Calculate start and end date of calendar.
    const startDate = new Date(year, monthIndex, 1);
    const endDate = new Date(year, monthIndex + 1, 0);
    {
      let weekdayIndex = startOfMonth.getDay();
      if (weekdayIndex > 0) {
        startDate.setDate(startOfMonth.getDate() - weekdayIndex);
      }
      weekdayIndex = endOfMonth.getDay();
      if (weekdayIndex < 6) {
        const offset = 6 - weekdayIndex;
        endDate.setDate(endOfMonth.getDate() + offset);
      }
    }
    const dayCount = helpers.getDaySpan(startDate, endDate);
    const weekCount = dayCount / 7;
    let monthNames;
    let dayNames;
    if (spartaStoreUtility.getData('currentMQ').mediaQueries.smallMid) {
      monthNames = UI_TEXT.monthNames;
      dayNames = UI_TEXT.dayNamesShort;
    } else {
      monthNames = UI_TEXT.monthNamesShort;
      dayNames = UI_TEXT.dayNamesMin;
    }
    const prevClass = isPrevMonthAvailable ? '' : `${FLYOUT_PREV_CLASS}--disabled`;
    const nextClass = isNextMonthAvailable ? '' : `${FLYOUT_NEXT_CLASS}--disabled`;

    // Header.
    let markup = `
      <div class="content-body" aria-modal="true" role="dialog">
        <div class="${FLYOUT_CLASS}-header ui-widget-header ui-helper-clearfix ui-corner-all">
          <button
            type="button"
            class="${FLYOUT_PREV_CLASS} ${prevClass}"
            title="${UI_TEXT.prevMonthText}"
            ${isPrevMonthAvailable ? '' : 'disabled'}
          >&nbsp;&nbsp;
            <span class="ada-hidden">${UI_TEXT.prevMonthView}</span>
          </button>
          <div class="${FLYOUT_CLASS}-title">
            <span class="${FLYOUT_CLASS}-month">${monthNames[monthIndex]}</span>
            <span class="${FLYOUT_CLASS}-year">${year}</span>
          </div>
          <button
            type="button"
            class="${FLYOUT_NEXT_CLASS} ${nextClass}"
            title="${UI_TEXT.nextMonthText}"
            ${isNextMonthAvailable ? '' : 'disabled'}
          >&nbsp;&nbsp;
            <span class="ada-hidden">${UI_TEXT.nextMonthView}</span>
          </button>
          <button
            type="button"
            title="${UI_TEXT.closeToolTipText}"
            class="${FLYOUT_CLOSE_CLASS}"
          >
            <span class="ada-hidden">${UI_TEXT.closeText}</span>
          </button>
        </div>
        <table class="${FLYOUT_CLASS}-calendar" role="grid" summary="${UI_TEXT.calendarSummary}">
          <thead>
            <tr>
    `;

    // Weekday name headings.
    for (let i = 0; i < dayNames.length; i++) {
      const cssTokens = [];
      if ([0, 6].includes(i) && options.showWeekend === 1) {
        cssTokens.push(`${FLYOUT_CLASS}-weekend`);
      }
      markup = `
        ${markup}
                <th scope="col" class="${FLYOUT_CLASS}-row-break ${cssTokens.join(' ')}">
                  <abbr title="${UI_TEXT.dayNames[i]}">
                    <span aria-hidden="true">${dayNames[i]}</span>
                    <span class="ada-hidden">${UI_TEXT.dayNames[i]}</span>
                  </abbr>
                </th>
      `;
    }
    markup = `
      ${markup}
            </tr>
          </thead>
          <tbody>
    `;

    // Body.
    const currentDate = startDate;
    let hasSelected = false;
    let currentWeekCheck = false;
    let allWeeksInMonth = [];
    for (let row = 0; row < weekCount; row++) {
      markup = `
        ${markup}
              <tr>
                <td class="${TOKEN_CLASS}-week" colspan="7">
      `;
      let isSelectableWeek = false;
      const isWeekInMinMaxRange = helpers.isWeekSelectable(currentDate, options);
      let currentWeek = [];
      for (let weekDay = 0; weekDay < 7; weekDay++) {
        const currentDay = currentDate.getDate();
        const currentMonth = monthNames[currentDate.getMonth()];
        const currentWeekday = dayNames[currentDate.getDay()];
        const holidayClass = helpers.isBankHoliday(currentDate) ? `${FLYOUT_CLASS}-holiday` : '';
        currentWeek.push(helpers.getMMDDYYYString(currentDate));
        if (isWeekInMinMaxRange && helpers.isWeekStartDate(currentDate, options)) {
          isSelectableWeek = true;
          const weekEndDate = helpers.getWeekEndDate(currentDate, options);
          const verboseWeekValue = helpers.getVerboseWeekString(currentDate, weekEndDate, UI_TEXT);
          const conciseWeekValue = helpers.getConciseWeekString(currentDate, weekEndDate);
          const isSelected = helpers.isValidDate(selectedEndDate) && helpers.isDateSame(selectedStartDate, currentDate);
          if (isSelected) {
            hasSelected = true;
          }
          const selectedWeekClass = isSelected ? `${TOKEN_CLASS}-item--selected` : '';
          markup = `
            ${markup}
                      <button
                        type="button"
                        class="${TOKEN_CLASS}-item ${selectedWeekClass}"
                        title="${verboseWeekValue}"
                        aria-label="${verboseWeekValue}"
                        data-value="${conciseWeekValue}"
                        tabindex="${isSelected ? 0 : -1}"
                        ${isSelected ? 'aria-selected="true"' : ''}
                      >
          `;
        }
        if (isWeekInMinMaxRange === false) {
          markup = `
            ${markup}
                        <span
                          class="
                            ${holidayClass}
                            ${FLYOUT_CLASS}--disabled
                            ${FLYOUT_CLASS}-unselectable
                          "
                        >
                          <span class="ada-hidden">${currentMonth}</span>
                          <span class="${STATE_CLASS}-default">${currentDay}</span>
                          <span class="ada-hidden"> ${UI_TEXT.notAvailDateToolTipText}</span>
                        </span>
          `;
        } else if ([0, 6].includes(weekDay) && options.showWeekend === 1) {
          markup = `
            ${markup}
                        <span
                          class="
                            ${holidayClass}
                            ${FLYOUT_CLASS}-weekend ${FLYOUT_CLASS}-past-weekend
                            ${FLYOUT_CLASS}-unselectable
                          "
                        >
                          <span class="ada-hidden">${currentMonth}</span>
                          <span class="${STATE_CLASS}-default">${currentDay}</span>
                          <span class="ada-hidden"> ${UI_TEXT.notAvailDateToolTipText}</span>
                        </span>
          `;
        } else {
          markup = `
            ${markup}
                        <span class="${holidayClass} ${TOKEN_CLASS}-day" aria-hidden="true">
                          <span class="ada-hidden">
                            ${UI_TEXT.selectDate} ${currentWeekday} ${currentMonth}
                          </span>
                          ${currentDay}
                        </span>
          `;
        }
        if (helpers.isWeekEndDate(currentDate, options) && isSelectableWeek) {
          isSelectableWeek = false;
          markup = `
            ${markup}
                      </button>
          `;
        }
        currentDate.setDate(currentDate.getDate() + 1);
      }
      allWeeksInMonth.push(currentWeek);
      markup = `
        ${markup}
                </td>
              </tr>
      `;
    }

    // Footer.
    markup = `
      ${markup}
          </tbody>
        </table>
        <div class="boa-ada-end ada-hidden">${UI_TEXT.endOfLayer}.</div>
      </div>
    `;
    $flyout.html(markup);

    // Enable the current week to be focused on initial load.
    if (options.focusCurrentWeek && !hasSelected) {
      allWeeksInMonth.forEach((week, index) => {
        if (week.includes(helpers.getMMDDYYYString(today))) {
          currentWeekCheck = true;
          const $grid = $flyout.find(`.${FLYOUT_CLASS}-calendar`);
          const $activeItem = $grid.find(`.${TOKEN_CLASS}-item`)[index];
          $activeItem.setAttribute('tabindex', 0);
        }
      });
    }

    // Ensure that one of the calendar items can receive focus.
    if (!hasSelected && !currentWeekCheck) {
      const $grid = $flyout.find(`.${FLYOUT_CLASS}-calendar`);
      const $activeItem = $grid.find(`.${TOKEN_CLASS}-item`).first();
      $activeItem.attr('tabindex', 0);
      $grid.attr('aria-activedescendant', $activeItem.attr('id'));
    }

    // Activate the available calendar items.
    $flyout.find(`.${TOKEN_CLASS}-item`).on('click', e => {
      e.stopPropagation();
      const $target = $(e.target);
      const selectedValue = $target.data('value') || $target.parent().data('value');
      setWeekSelection(selectedValue);
      setSelectedDates();
      $input.data(PROP_NAME, options);
      closeWeekFlyout();
      $input.focus();
    });

    // Allow any button in the flyout to close the monthpicker on blur.
    $flyout.find('button').on('blur', doBlur);
  }
  function monthFocus(flyout) {
    const focusablebutton = flyout.find('button[tabindex=0]');
    // Focus month after flyout is fully open.
    setTimeout(() => {
      focusablebutton.focus();
    }, 500);
    options.focusCurrentWeek = false;
  }
  function showPrevMonth() {
    const current = options.currentMonthView;
    options.currentMonthView = new Date(current.getFullYear(), current.getMonth() - 1, 1);
    if (options.currentMonthView.getTime() >= helpers.getFirstOfMonth(options.minDate).getTime()) {
      renderFlyout();
      return true;
    }
    options.currentMonthView = current;
    return false;
  }
  function showNextMonth() {
    const current = options.currentMonthView;
    options.currentMonthView = new Date(current.getFullYear(), current.getMonth() + 1, 0);
    options.currentMonthView.setDate(options.currentMonthView.getDate() + 1);
    if (helpers.getFirstOfMonth(options.currentMonthView).getTime() <= helpers.getFirstOfMonth(options.maxDate).getTime()) {
      renderFlyout();
      return true;
    }
    options.currentMonthView = current;
    return false;
  }

  // Sets the focus to a given week (if specified) or the first focusable week.
  function selectWeek($item = null) {
    // Make all of the items unfocusable.
    const $grid = $item.closest(`.${FLYOUT_CLASS}-calendar`);
    const $items = $grid.find(`.${TOKEN_CLASS}-item`);
    $items.attr('tabindex', -1);

    // Find a cell to select if none was supplied.
    let $viableCell = $item || $items.find(`.${STATE_CLASS}-active`);
    $viableCell = $viableCell.first();

    // Make only the selected week (or first viable week) focusable.
    $viableCell.attr('tabindex', 0).focus();
    $grid.attr('aria-activedescendant', $viableCell.attr('id'));
  }
  function initHandlers() {
    // Get this calendar icon button and input tied to the weekpicker.
    const $triggerButton = $input.siblings(`.${datepicker.triggerClass}`);
    const mainDivId = `${datepicker.flyoutId}-${TOKEN_CLASS}`;
    function moveFocus(dir) {
      let monthChanged = false;

      // Determine which item should receive focus.
      const $focusedItem = $flyout.find(`.${TOKEN_CLASS}-item:focus`);
      if ($focusedItem.length) {
        const $row = $focusedItem.closest('tr');
        let $rows = $row.parent().find('tr');
        let rowIndex = $rows.index($row);
        if (dir === 'up') {
          if (rowIndex > 0) {
            rowIndex -= 1;
          } else {
            monthChanged = showPrevMonth();
          }
        } else if (dir === 'down') {
          if (rowIndex < $rows.length - 1) {
            rowIndex += 1;
          } else {
            monthChanged = showNextMonth();
          }
        }

        // If no viable cell was found and the month was changed, update references.
        if (monthChanged) {
          $rows = $flyout.find('tbody tr');
          rowIndex = dir === 'up' ? $rows.length - 1 : 0;
        }

        // Move the focus to the appropriate item.
        const $weekToFocus = $rows.eq(rowIndex).find(`.${TOKEN_CLASS}-item`);
        if ($weekToFocus) {
          selectWeek($weekToFocus);
          return;
        }
      }

      // If no week was in focus, just focus on the first viable week.
      selectWeek();
    }
    function handleArrowDown(event) {
      let eventHandled = false;
      let moveFocusToGrid = false;
      if ($flyout.is(':hidden')) {
        // If it's not open already, open the flyout.
        $triggerButton.trigger('click');
        moveFocusToGrid = true;
      } else if (event.target.classList.contains(`${TOKEN_CLASS}-item`)) {
        moveFocus('down');
        eventHandled = true;
      } else {
        // If the down arrow was pressed on a flyout control, move the focus into the grid.
        moveFocusToGrid = true;
      }
      if (moveFocusToGrid) {
        let focusElement = $(`.${TOKEN_CLASS}-item--selected`);
        if (!focusElement.length) {
          focusElement = $(`.${TOKEN_CLASS}-item:first`);
        }
        focusElement.focus();
        eventHandled = true;
      }
      return eventHandled;
    }
    function handleEscape() {
      closeWeekFlyout();
      closeMonthFlyout();
    }
    function doKeyDown(event) {
      let eventHandled = false;
      const noAltShiftOrCtrl = !event.altKey && !event.shiftKey && !event.ctrlKey;
      if (event.key === 'Escape') {
        handleEscape();
        eventHandled = true;
      } else if (event.key === 'Tab') {
        helpers.focusTrap($flyout, event);
      } else if (noAltShiftOrCtrl) {
        if (event.key === 'ArrowUp') {
          moveFocus('up');
          eventHandled = true;
        } else if (event.key === 'ArrowDown') {
          eventHandled = handleArrowDown(event);
        }
      }
      if (eventHandled) {
        event.preventDefault();
        event.stopPropagation();
      }
    }
    $input.off('keydown').on('keydown', doKeyDown).off('blur').on('blur', doBlur);
    $triggerButton.attr('aria-controls', mainDivId).off('click').on('click', e => {
      e.stopPropagation();

      // Just close the flyout if it's already visible.
      if ($flyout.is(':visible')) {
        closeWeekFlyout();
        return;
      }

      // Close other flyouts if we're opening a new one.
      closeMonthFlyout();
      closeDateFlyout();
      $(`.${TOKEN_CLASS}`).not($flyout).hide('medium');

      // Open the flyout.
      options.prevVal = $input.val();
      setSelectedDates();
      options.focusCurrentWeek = true;
      renderFlyout();
      helpers.positionFlyout($flyout, $input);
      $flyout.show('medium');
      monthFocus($flyout);
    }).off('keydown').on('keydown', doKeyDown).off('blur').on('blur', doBlur);
    $flyout.off('click').on('click', event => {
      event.stopPropagation();
      const {
        classList
      } = event.target;
      if (classList.contains(FLYOUT_PREV_CLASS)) {
        showPrevMonth();
        let $buttonToFocus = $flyout.find(`.${FLYOUT_PREV_CLASS}`);
        if ($buttonToFocus.is(':disabled')) {
          $buttonToFocus = $flyout.find(`.${FLYOUT_NEXT_CLASS}`);
        }
        $buttonToFocus.focus();
      } else if (classList.contains(FLYOUT_NEXT_CLASS)) {
        showNextMonth();
        let $buttonToFocus = $flyout.find(`.${FLYOUT_NEXT_CLASS}`);
        if ($buttonToFocus.is(':disabled')) {
          $buttonToFocus = $flyout.find(`.${FLYOUT_PREV_CLASS}`);
        }
        $buttonToFocus.focus();
      } else if (classList.contains(FLYOUT_CLOSE_CLASS)) {
        closeWeekFlyout();
      }
    }).off('keydown').on('keydown', doKeyDown);
    $(document).on('click', () => {
      closeWeekFlyout();
      closeMonthFlyout();
    });
  }
  function initOptions() {
    helpers.setupMinMaxDates(options);
    setSelectedDates();
    options.today = helpers.getToday();
    options.currentMonthView = null;
  }
  function init() {
    // If weekpicker is already attached to current input, do not proceed any further.
    if ($input.next().hasClass(TOKEN_CLASS)) {
      return;
    }
    const TEMPLATE = helpers.getFlyoutTemplate(TOKEN_CLASS);
    $flyout = $(TEMPLATE).insertAfter($input.siblings().last());
    initOptions();
    initHandlers();
  }
  init();
}

/* eslint-disable require-jsdoc */
let datepicker = null;
function getCurrentDate() {
  let currMonth = (new Date().getMonth() + 1).toString();
  let currDay = new Date().getDate().toString();
  const currYear = new Date().getFullYear().toString();
  if (currMonth.length === 1) {
    currMonth = `0${currMonth}`;
  }
  if (currDay.length === 1) {
    currDay = `0${currDay}`;
  }
  return `${currMonth}/${currDay}/${currYear}`;
}
const currentDate = getCurrentDate();
const FLYOUT_CLASS = 'ui-datepicker';
const STATE_CLASS = 'ui-state';

// Check if date is today and add corresponding class.
function checkToday(dateClass, dateText) {
  if (dateText === currentDate) {
    dateClass = `${FLYOUT_CLASS}-today ${FLYOUT_CLASS}-current-day`;
  }
  return dateClass;
}

// Standard bank holiday function.
function disableBankHolidays(dateClass, dateText, bankHolidays) {
  if ($.inArray(dateText, bankHolidays) !== -1) {
    dateClass = `
      ${dateClass}
      ${FLYOUT_CLASS}-unselectable
      ${STATE_CLASS}-disabled
      ${FLYOUT_CLASS}-holiday
    `;
  }
  return dateClass;
}

// Check if date is due and add corresponding class.
function checkDueDate(dateClass, dateText, dueDate) {
  if (dateText === dueDate) {
    dateClass = `
      ${dateClass}
      ${FLYOUT_CLASS}-due
    `;
  }
  return dateClass;
}

// Check if date has indicator and add corresponding class.
function checkIndicator(dateClass, dateText, indicator) {
  if ($.inArray(dateText, indicator) !== -1) {
    dateClass = `
      ${dateClass}
      ${FLYOUT_CLASS}-indicate
    `;
  }
  return dateClass;
}

// Standard method to process different checks and return classes.
function processStandardDateAttributes(bankHolidays, date, dueDates, indicator, showHoliday) {
  const pad = unit => unit.length === 1 ? `0${unit}` : unit;
  const month = pad((date.getMonth() + 1).toString());
  const day = pad(date.getDate().toString());
  let dateText = '';
  let dateClass = '';
  dateText = `${month}/${day}/${date.getFullYear().toString()}`;
  dateClass = checkToday(dateClass, dateText);
  if (showHoliday) {
    dateClass = disableBankHolidays(dateClass, dateText, bankHolidays);
  }
  dateClass = checkDueDate(dateClass, dateText, dueDates);
  dateClass = checkIndicator(dateClass, dateText, indicator);
  return [true, dateClass];
}
const defaultOptions = {
  selector: '.datepicker',
  // Optional. Class selector for the datepicker.
  datepickerType: 'day',
  // Determines type of datepicker. (day | range | week | month)
  rangeStartSelector: null,
  // Only applicable when `datepickerType` is set to `range`.
  rangeEndSelector: null,
  // Only applicable when `datepickerType` is set to `range`.
  changeMonth: false,
  changeYear: false,
  // Date config defaults:
  numberOfMonths: 1,
  // Optional. Number of months to display at one time.
  minDate: '-1y',
  // See `http://api.jqueryui.com/1.8/datepicker/#option-minDate`.
  maxDate: '+12m +1d',
  // See `http://api.jqueryui.com/1.8/datepicker/#option-maxDate`.
  dateFormat: 'mm/dd/yy',
  // See `http://api.jqueryui.com/1.8/datepicker/#option-dateFormat`.

  // Other options:
  altField: '',
  // Changes value of antother field.
  hideIfNoPrevNext: false,
  // Previous and next links are hidden when not applicable.
  showOtherMonths: true,
  // Whether to display dates in other months (non-selectable) at the start
  // or end of the current month. Note: `selectOtherMonths` not supported.
  showWeekend: 1,
  // 0: weekends available; 1: weekends grayed out.
  showHoliday: 1,
  // 0: Bank holidays available; 1: Bank holidays grayed out.
  showWeekendIfToday: false,
  // Optional. To enable weekend date if today's date is a weekend.
  beforeShowDay: processStandardDateAttributes,
  // Optional custom holiday function.
  endOfMonthLink: false,
  // Adds a useless end of month link
  altFormat: 'mm/dd/yy',
  // Allows one date format to be shown to the user while a different format
  // is actually behind the scenes.
  focusCurrentWeek: true,
  // Focus current week on opening weekpicker.

  // Icon display:
  maxlength: 10,
  // Added in Sparta 2 Datepicker 1.0.0:
  addBankHolidays: [],
  // Append dates onto the `bankHolidays` array.
  replaceBankHolidays: null,
  // Replace the `bankHolidays` array.
  skin: 'default-design',
  // Controlled from the helper JCR via the `calendarSkin` property.
  onChange: null
};
function addADAMessage(triggerButtonText, $dpInput) {
  // Add a little more label text to the flyout trigger button.
  const triggerButton = $dpInput.siblings('button').get(0);
  if (triggerButton) {
    triggerButton.setAttribute('aria-label', `${triggerButtonText} ${triggerButton.getAttribute('aria-label')}`);
  }
}
function addInstanceAdaEmbellishments($dpInput, opts) {
  const {
    useDateFormat,
    downArrowCalendarLayer,
    doubleTapCalendarLayer,
    triggerButtonText
  } = uiText[datepicker.language];
  spartaStoreUtility.getData('browser');

  // Update ADA hidden element containing date format to be announced.
  const hiddenEl = `
    <span class="ada-hidden"></span>
  `;
  $dpInput.siblings('label').append(hiddenEl);
  addADAMessage(triggerButtonText, $dpInput);
}
function setupDatepicker(selector, options) {
  const opts = $.extend(true, {}, options, {
    selector
  });
  const $dpInput = $(selector);

  // Skin passed in through config options takes precedence over JCR.
  opts.skin = `spa-date-input--${options && options.skin || $dpInput.closest('[data-calendar-skin]').attr('data-calendar-skin')}`;
  if (opts.datepickerType.toLocaleLowerCase() === 'week') {
    weekpicker($dpInput, opts);
  } else if (opts.datepickerType.toLocaleLowerCase() === 'month') {
    monthpicker($dpInput, opts);
  } else {
    $dpInput.datepicker(opts).attr('maxLength', opts.maxlength);
  }
  // Date picker auto formating
  if (opts.dateFormat === 'mm/dd/yy' && opts.datepickerType.toLocaleLowerCase() !== 'month' && opts.datepickerType.toLocaleLowerCase() !== 'week') {
    spartaInputFormattingUtility.formatInput($dpInput[0], 'date');
  }
  addInstanceAdaEmbellishments($dpInput);
  return $dpInput;
}

// Create a new DP instance by passing in an `options` object. See the demo for details.
const DatePicker = {
  init(options) {
    datepicker = core();
    const opts = $.extend(true, {}, defaultOptions, options);

    // Maintain backward compatibility for these misnamed parameters.
    if (opts.id) {
      opts.selector = opts.id;
    }
    if (opts.rangeStartId) {
      opts.rangeStartSelector = opts.rangeStartId;
    }
    if (opts.rangeEndId) {
      opts.rangeEndSelector = opts.rangeEndId;
    }
    if (opts.datepickerType === 'range' && opts.rangeStartSelector && opts.rangeEndSelector && $(opts.rangeStartSelector).data('datepicker') && $(opts.rangeEndSelector).data('datepicker')) {
      return [$(opts.rangeStartSelector), $(opts.rangeEndSelector)];
    }

    // Switch the language based on parameter.
    const language = spartaGlobalFunctions.getLanguage();
    datepicker.language = language ? language.split('-')[0] : 'en';
    if (datepicker.language === 'es') {
      $.extend(datepicker.defaults, datepicker.regional['es-us']);
    } else {
      $.extend(datepicker.defaults, datepicker.regional['']);
    }

    // Add a `disabled` attribute and class if `options` has the `disabled` attribute as true.
    if (opts.disabled) {
      const {
        triggerButtonText
      } = uiText[datepicker.language];
      const $dpInput = $(opts.selector);
      addADAMessage(triggerButtonText, $dpInput);
      const inputText = document.querySelector(options.selector);
      if (inputText) {
        inputText.disabled = true;
        const calendarIcon = inputText.parentElement.querySelector('.datepicker-icon--calendar');
        if (calendarIcon) {
          calendarIcon.classList.add('datepicker-icon--calendar__disabled');
          calendarIcon.parentElement.disabled = true;
        }
      }
    }
    const defaultBankHolidays = spartaStoreUtility.getData('bankHolidays');

    // Scope `bankHolidays` to current datepicker instance.
    const bankHolidays = opts.replaceBankHolidays ? $.makeArray(opts.replaceBankHolidays) : defaultBankHolidays.concat(opts.addBankHolidays);
    opts.beforeShowDay = opts.beforeShowDay.bind(this, bankHolidays);
    if (!opts.disabled) {
      if (typeof opts.datepickerType === 'string' && opts.datepickerType.toLocaleLowerCase() === 'range') {
        const startDatepicker = setupDatepicker(opts.rangeStartSelector, opts);
        const endDatepicker = setupDatepicker(opts.rangeEndSelector, opts);

        // Return a tuple/array of [start, end] input DP instance.
        return [startDatepicker, endDatepicker];
      }

      // Return the DP input instance.
      return setupDatepicker(opts.selector, opts);
    }
    return null;
  }
};
class DatepickerUtility {
  constructor() {
    this.init = DatePicker.init;
  }
}
spartaSingletonUtility('SpartaDatePickerUtility', DatepickerUtility);

export { DatePicker as default };
